import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

// ─────────── Tenant sub-org tree (from PR #899) ───────────
export type OrgKind = 'GROUP' | 'UNIVERSITY' | 'COLLEGE' | 'DEPARTMENT';

export interface OrgTreeNode {
  _id: string;
  name: string;
  kind: OrgKind;
  level: number;
  parentTenantId?: string;
  status: string;
  adminCount: number;
  children: OrgTreeNode[];
}
export function useOrgTree() {
  return useQuery({
    queryKey: ['tenant', 'orgs', 'tree'],
    queryFn: () => apiGet<{ tree: OrgTreeNode }>('/tenant/orgs/tree'),
  });
}
export function useOrgAuthority() {
  return useQuery({
    queryKey: ['tenant', 'orgs', 'authority'],
    queryFn: () => apiGet<{ parentKind: OrgKind; canCreate: OrgKind[] }>('/tenant/orgs/authority'),
  });
}

export interface PendingInviteRow {
  _id: string;
  tenant: string;
  tenantName: string;
  tenantKind: OrgKind;
  email: string;
  role: string;
  fullName: string | null;
  sentAt: string;
  expiresAt: string;
  expiresInDays: number;
}
export function usePendingInvites() {
  return useQuery({
    queryKey: ['tenant', 'invites'],
    queryFn: () => apiGet<{ rows: PendingInviteRow[] }>('/tenant/invites'),
  });
}
export function useResendTenantInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ resent: boolean; inviteId: string; expiresAt: string }>(`/tenant/invites/${id}/resend`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', 'invites'] }),
  });
}
export function useRevokeTenantInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ revoked: boolean; inviteId: string }>(`/tenant/invites/${id}/revoke`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', 'invites'] }),
  });
}
export interface CreateOrgBody {
  name: string;
  adminName: string;
  adminEmail: string;
  aisheCode?: string;
  city?: string;
  affiliatedUniversityId?: string;
  collegeId?: string;
}
export interface CreateOrgResult {
  tenantId: string;
  tenantName: string;
  tenantKind: OrgKind;
  adminUserId: string;
  adminEmail: string;
  adminRole: string;
  invite: { id: string; acceptUrl: string; expiresAt: string; emailDelivered: boolean };
}
export function useCreateChildOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { childKind: OrgKind; body: CreateOrgBody }) => {
      const path =
        input.childKind === 'UNIVERSITY' ? '/tenant/orgs/universities'
          : input.childKind === 'COLLEGE' ? '/tenant/orgs/colleges'
            : '/tenant/orgs/departments';
      return apiPost<CreateOrgResult>(path, input.body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', 'orgs', 'tree'] });
      qc.invalidateQueries({ queryKey: ['group', 'colleges'] });
    },
  });
}
const AUTHORITY_TABLE: Record<OrgKind, OrgKind[]> = {
  GROUP: ['UNIVERSITY', 'COLLEGE', 'DEPARTMENT'],
  UNIVERSITY: ['COLLEGE', 'DEPARTMENT'],
  COLLEGE: ['DEPARTMENT'],
  DEPARTMENT: [],
};
export function allowedChildKinds(parentKind: OrgKind): OrgKind[] {
  return AUTHORITY_TABLE[parentKind] ?? [];
}

// ─────────── Magic-link sign-in (from PR #899) ───────────
export function useRequestSignInLink() {
  return useMutation({
    mutationFn: (email: string) =>
      apiPost<{ requested: boolean; emailDelivered?: boolean; emailMessageId?: string }>(
        '/auth/signin-link/request',
        { email },
      ),
  });
}
export function useInspectSignInLink(token: string | undefined) {
  return useQuery({
    queryKey: ['signin-link', token],
    queryFn: () =>
      apiGet<{ type: 'signin_link'; email: string; fullName?: string; role: string; expiresAt: string }>(
        `/auth/signin-link/${token}`,
      ),
    enabled: !!token,
    retry: false,
  });
}
export function useConsumeSignInLink() {
  return useMutation({
    mutationFn: (token: string) =>
      apiPost<{
        user: { id: string; fullName: string; email: string; role: string; tenant: string; onboardingComplete: boolean };
        activeTenantId: string | null;
        memberships: unknown[];
        tokens: { accessToken: string; refreshToken: string; expiresIn: number };
      }>('/auth/signin-link/consume', { token }),
  });
}
import { apiGet, apiPost, apiPatch, apiDelete } from './api';
import { useAuthStore } from './authStore';

/**
 * Central query hooks. Each key is a stable tuple so cache invalidation on a mutation is trivial:
 *   queryClient.invalidateQueries({ queryKey: ['drives'] })
 *
 * Server responses are unwrapped via apiGet/apiPost (the `data` field of the envelope).
 *
 * MULTI-TENANT INVARIANT (F-Step 104, Trap 2 defense):
 *
 * Every workspace switch calls `queryClient.clear()` before returning (see
 * `useSwitchIfNeeded` in `./useSwitchIfNeeded.ts`). That is the primary flush that
 * prevents cross-tenant cache leakage — cached lists from workspace A never render
 * in workspace B because the whole cache is emptied on switch.
 *
 * As belt-and-suspenders, tenant-scoped hooks below ALSO include `activeTenantId` as
 * a queryKey segment. If a future refactor removes `clear()`, keys still isolate
 * results per tenant.
 *
 * Rule:
 *   - Tenant-scoped (drives, applications, offers, opportunities, interviews, calendar,
 *     tpo/*, group/*, hod/*, faculty/*, principal/*, company/*, workflow-runs, tenant-billing)
 *     → `queryKey: [..., activeTenantId, ...]`
 *   - Identity-level (notifications, /me, memberships, skill-taxonomy, dpdp/*,
 *     student/prep-plan, student/profile-360, resumes, mock, badges) → NO activeTenantId.
 *   - Platform-ops (platform/*) → NO activeTenantId — cross-tenant by design.
 */

/** Read the current active tenant id for queryKey scoping. Empty string = pre-hydration. */
function useKeyScope(): string {
  return useAuthStore((s) => s.activeTenantId) ?? '';
}

// -- Drives --------------------------------------------------------

export interface DriveListItem {
  _id: string;
  driveName: string;
  driveDate: string;
  tier: 0 | 1 | 2;
  status: string;
  partner?: { name: string; logo?: string };
  opportunity?: { title: string; type: string };
  college?: { name: string };
  results?: { registered: number; offered: number };
}

export function useDrives(opts?: { status?: string }) {
  const scope = useKeyScope();
  return useQuery({
    queryKey: ['drives', scope, opts?.status ?? 'all'],
    queryFn: () => apiGet<{ drives: DriveListItem[] }>(`/drives${opts?.status ? `?status=${opts.status}` : ''}`),
  });
}

export function useDrive(id: string | undefined) {
  const scope = useKeyScope();
  return useQuery({
    queryKey: ['drives', scope, id],
    enabled: !!id,
    queryFn: () => apiGet<{ drive: DriveListItem; stageCounts: Array<{ _id: string; count: number }> }>(`/drives/${id}`),
  });
}

export function useCreateDrive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/drives', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drives'] }),
  });
}

export interface DriveEligibleCount {
  driveId: string;
  eligibleCount: number;
  appliedCount: number;
  yieldPct: number;
}
export function useDriveEligibleCount(id: string | undefined) {
  return useQuery({
    queryKey: ['drives', 'eligible-count', id],
    enabled: !!id,
    queryFn: () => apiGet<DriveEligibleCount>(`/drives/${id}/eligible-count`),
  });
}

export interface DriveFunnel {
  driveId: string;
  eligibleCount: number | null;
  applied: number;
  shortlisted: number;
  interviewsScheduled: number;
  offered: number;
  accepted: number;
  byStatus: Record<string, number>;
  offers: Record<string, number>;
}
export function useDriveFunnel(id: string | undefined) {
  return useQuery({
    queryKey: ['drives', 'funnel', id],
    enabled: !!id,
    queryFn: () => apiGet<DriveFunnel>(`/drives/${id}/funnel`),
  });
}

// -- Applications --------------------------------------------------

export interface ApplicationListItem {
  _id: string;
  status: string;
  appliedAt: string;
  student?: { fullName?: string; email?: string };
  drive?: { driveName?: string; driveDate?: string };
}

// ────────────────────────── F76 Interview detail + reschedule/feedback ──────────────────────────
export interface InterviewDetail {
  _id: string;
  application: string;
  drive: { _id: string; driveName: string; driveDate: string; college?: { name: string } } | string;
  partner?: { name: string };
  roundNumber: number;
  roundType?: string;
  mode: string;
  scheduledAt: string;
  duration?: number;
  endAt?: string;
  room?: string;
  meetingLink?: string;
  status: string;
  rescheduleReason?: string;
  interviewers?: string[];
  feedback?: {
    rating?: number;
    strengths?: string[];
    weaknesses?: string[];
    recommendation?: string;
    remarks?: string;
    result?: string;
    submittedBy?: string;
    submittedAt?: string;
  };
}
export interface InterviewListItem {
  _id: string;
  scheduledAt: string;
  endAt?: string;
  duration?: number;
  room?: string;
  mode: string;
  status: string;
  roundNumber: number;
  roundType?: string;
  meetingLink?: string;
  partner?: { name?: string };
  drive?: { driveName?: string; college?: { name?: string } };
  feedback?: { result?: string };
  rescheduledFrom?: string;
  rescheduleReason?: string;
  panel?: string;
  result?: string;
}
/**
 * useInterviews — Pass 2 canonical list.
 * Query params: page/limit/q/drive/application/student/status/mode/roundType/from/to/
 *   sortBy/sortDir. Server returns:
 *   `{interviews, summary: {byStatus}} + {page,limit,total}` meta.
 *
 * `opts.student` is a legacy client-side hint; the server auto-scopes to `req.auth.sub`
 * when the caller's role is student. It's still forwarded as a cache-key discriminator.
 */
export interface InterviewsListOpts {
  page?: number;
  limit?: number;
  q?: string;
  drive?: string;
  application?: string;
  student?: string | boolean;
  /** Panel workspace filter — scopes to interviews where the caller is on the interviewers[] panel. */
  mine?: boolean;
  status?: string;
  mode?: string;
  roundType?: string;
  from?: string;
  to?: string;
  sortBy?: 'scheduledAt' | 'roundNumber' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}
export interface InterviewsListResponse {
  interviews: InterviewListItem[];
  summary?: { byStatus?: Record<string, number> };
}
export function useInterviews(opts?: InterviewsListOpts) {
  const scope = useKeyScope();
  const qs = new URLSearchParams();
  if (opts?.page) qs.set('page', String(opts.page));
  if (opts?.limit) qs.set('limit', String(opts.limit));
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.drive) qs.set('drive', opts.drive);
  if (opts?.application) qs.set('application', opts.application);
  if (typeof opts?.student === 'string') qs.set('student', opts.student);
  if (opts?.mine) qs.set('mine', 'true');
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.mode) qs.set('mode', opts.mode);
  if (opts?.roundType) qs.set('roundType', opts.roundType);
  if (opts?.from) qs.set('from', opts.from);
  if (opts?.to) qs.set('to', opts.to);
  if (opts?.sortBy) qs.set('sortBy', opts.sortBy);
  if (opts?.sortDir) qs.set('sortDir', opts.sortDir);
  const q = qs.toString();
  return useQuery({
    queryKey: ['interviews', scope, q, opts?.student === true ? 'me' : ''],
    queryFn: () => apiGet<InterviewsListResponse>(`/interviews${q ? `?${q}` : ''}`),
  });
}
export function useInterview(id: string | undefined) {
  const scope = useKeyScope();
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['interview', scope, id],
    queryFn: () => apiGet<{ interview: InterviewDetail }>(`/interviews/${id}`),
  });
}
export function useRequestReschedule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: 'exam_clash' | 'other_interview' | 'health_family' | 'other'; note?: string; preferredSlots?: string[] }) =>
      apiPost(`/interviews/${id}/reschedule-request`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['interview', id] }),
  });
}
export function useSubmitInterviewFeedback(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      rating: number;
      strengths?: string[];
      weaknesses?: string[];
      recommendation?: 'Strongly_Recommend' | 'Recommend' | 'Neutral' | 'Not_Recommend' | 'Strongly_Not_Recommend';
      remarks?: string;
      result: 'Selected' | 'Rejected' | 'On_Hold' | 'Needs_Another_Round' | 'Pending';
    }) => apiPost<{ interview: InterviewDetail }>(`/interviews/${id}/feedback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interview', id] });
      qc.invalidateQueries({ queryKey: ['interviews'] });
    },
  });
}

/**
 * useApplications — Pass 4 canonical list.
 * Query params supported: page/limit/q/status/drive/opportunity/student/partner/
 *   matchMin/matchMax/from/to/sortBy/sortDir. Server returns:
 *   `{applications, summary: {byStatus}} + {page, limit, total}` meta.
 */
export interface ApplicationsListOpts {
  page?: number;
  limit?: number;
  q?: string;
  status?: string;
  drive?: string;
  opportunity?: string;
  student?: string;
  partner?: string;
  matchMin?: number;
  matchMax?: number;
  sortBy?: 'appliedAt' | 'createdAt' | 'updatedAt' | 'matchScore.overall' | 'status';
  sortDir?: 'asc' | 'desc';
}
export interface ApplicationsListResponse {
  applications: (ApplicationListItem & { matchScore?: { overall?: number } })[];
  summary?: { byStatus?: Record<string, number> };
}
export function useApplications(opts?: ApplicationsListOpts) {
  const scope = useKeyScope();
  const qs = new URLSearchParams();
  if (opts?.page) qs.set('page', String(opts.page));
  if (opts?.limit) qs.set('limit', String(opts.limit));
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.drive) qs.set('drive', opts.drive);
  if (opts?.opportunity) qs.set('opportunity', opts.opportunity);
  if (opts?.student) qs.set('student', opts.student);
  if (opts?.partner) qs.set('partner', opts.partner);
  if (typeof opts?.matchMin === 'number') qs.set('matchMin', String(opts.matchMin));
  if (typeof opts?.matchMax === 'number') qs.set('matchMax', String(opts.matchMax));
  if (opts?.sortBy) qs.set('sortBy', opts.sortBy);
  if (opts?.sortDir) qs.set('sortDir', opts.sortDir);
  const q = qs.toString();
  return useQuery({
    queryKey: ['applications', scope, q],
    queryFn: () => apiGet<ApplicationsListResponse>(`/applications${q ? `?${q}` : ''}`),
  });
}

// GET /applications/:id/timeline — Pass 10 unified event feed for one application.
export interface ApplicationTimelineEvent {
  at: string;
  kind: 'status' | 'interview' | 'offer' | 'badge' | 'workflow' | 'attempt' | 'created';
  title: string;
  detail?: string;
  meta?: Record<string, unknown>;
}
export function useApplicationTimeline(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['application-timeline', id],
    queryFn: () => apiGet<{ applicationId: string; currentStatus: string; events: ApplicationTimelineEvent[] }>(
      `/applications/${id}/timeline`,
    ),
  });
}

// PATCH /applications/:id — status transitions (withdraw, staff moves).
export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; status: string; note?: string }) =>
      apiPatch(`/applications/${body.id}`, { status: body.status, note: body.note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['application-timeline'] });
      qc.invalidateQueries({ queryKey: ['student', 'dashboard'] });
    },
  });
}

export function useApplyToDrive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { drive: string; resume?: string; coverLetter?: string; customAnswers?: Record<string, string> }) => apiPost('/applications', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['applications'] }),
  });
}

export function useDrivesForOpportunity(opportunityId: string | undefined) {
  const scope = useKeyScope();
  return useQuery({
    enabled: Boolean(opportunityId),
    queryKey: ['drives', scope, 'opportunity', opportunityId],
    queryFn: () => apiGet<{ drives: Array<{ _id: string; driveName: string; driveDate: string; status: string; college?: { name: string; code: string }; applicationsCloseAt?: string }> }>(`/drives?opportunity=${opportunityId}`),
  });
}

export function useMoveApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: string; note?: string }) =>
      apiPatch(`/applications/${input.id}`, { status: input.status, note: input.note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['applications'] }),
  });
}

export function useBulkMoveApplications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[]; status: string; note?: string }) =>
      apiPost('/applications/bulk-move', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['applications'] }),
  });
}

// -- Offers --------------------------------------------------------

export interface OfferListItem {
  _id: string;
  application: string;                  // ObjectId — used by v1.6 §7.6 WorkflowRun hook
  status: string;
  role?: string;
  ctc?: { amountInr: number };
  allChecksPassed?: boolean;
  checks?: Array<{ key: string; result: 'pass' | 'warn' | 'fail'; note?: string; suggestion?: string }>;
  letterUrl?: string;
  deadlineAt?: string;
  /** Backward-compat alias for the older `deadline` field name. */
  deadline?: string;
  undoWindowEndsAt?: string;
  student?: { fullName: string; email: string };
  partner?: { name: string };
  drive?: { driveName: string };
}

/**
 * useOffers — Pass 2 canonical list.
 * Query params: page/limit/q/status/partner/drive/opportunity/ctcMin/ctcMax/
 *   deadlineBefore/After/passedChecks/sortBy/sortDir. Server returns:
 *   `{offers, summary: {byStatus}} + {page,limit,total}` meta.
 */
export interface OffersListOpts {
  page?: number;
  limit?: number;
  q?: string;
  status?: string;
  partner?: string;
  drive?: string;
  opportunity?: string;
  ctcMin?: number;
  ctcMax?: number;
  deadlineAfter?: string;
  deadlineBefore?: string;
  passedChecks?: boolean;
  sortBy?: 'createdAt' | 'releasedAt' | 'acceptedAt' | 'deadlineAt' | 'ctc.amountInr';
  sortDir?: 'asc' | 'desc';
}
export interface OffersListResponse {
  offers: OfferListItem[];
  summary?: { byStatus?: Record<string, number> };
}
export function useOffers(opts?: OffersListOpts) {
  const scope = useKeyScope();
  const qs = new URLSearchParams();
  if (opts?.page) qs.set('page', String(opts.page));
  if (opts?.limit) qs.set('limit', String(opts.limit));
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.partner) qs.set('partner', opts.partner);
  if (opts?.drive) qs.set('drive', opts.drive);
  if (opts?.opportunity) qs.set('opportunity', opts.opportunity);
  if (typeof opts?.ctcMin === 'number') qs.set('ctcMin', String(opts.ctcMin));
  if (typeof opts?.ctcMax === 'number') qs.set('ctcMax', String(opts.ctcMax));
  if (opts?.deadlineAfter) qs.set('deadlineAfter', opts.deadlineAfter);
  if (opts?.deadlineBefore) qs.set('deadlineBefore', opts.deadlineBefore);
  if (typeof opts?.passedChecks === 'boolean') qs.set('passedChecks', String(opts.passedChecks));
  if (opts?.sortBy) qs.set('sortBy', opts.sortBy);
  if (opts?.sortDir) qs.set('sortDir', opts.sortDir);
  const q = qs.toString();
  return useQuery({
    queryKey: ['offers', scope, q],
    queryFn: () => apiGet<OffersListResponse>(`/offers${q ? `?${q}` : ''}`),
  });
}

// GET /offers/:id/checks — Pass 11 per-offer 4-check breakdown.
export interface OfferCheck {
  key: 'name_vs_roster' | 'ctc_vs_drive' | 'placement_policy' | 'authorised_signatory';
  result: 'pass' | 'warn' | 'fail';
  note?: string;
  suggestion?: string;
  checkedAt?: string;
}
export interface OfferChecksResponse {
  offerId: string;
  status: string;
  allChecksPassed: boolean;
  checks: OfferCheck[];
  canReleaseNow: boolean;
  letterUploadedAt: string | null;
}
export function useOfferChecks(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['offer-checks', id],
    queryFn: () => apiGet<OfferChecksResponse>(`/offers/${id}/checks`),
  });
}
/** Rerun-response shape mirrors the Offer.checks sub-doc — passed:boolean, not the
 *  GET /checks projection which uses result:'pass'|'warn'|'fail'. */
export interface OfferCheckRerun {
  key: 'name_vs_roster' | 'ctc_vs_drive' | 'placement_policy' | 'authorised_signatory';
  passed: boolean;
  note?: string;
  suggestion?: string;
  checkedAt?: string;
}
export function useRerunOfferChecks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ offerId: string; allChecksPassed: boolean; checks: OfferCheckRerun[] }>(`/offers/${id}/checks/rerun`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['offer-checks', id] });
      qc.invalidateQueries({ queryKey: ['offers'] });
    },
  });
}

// Student offer decisions — accept / undo / decline.
export function useAcceptOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; signedName: string; acks?: string[] }) =>
      apiPost<{ offer: OfferListItem }>(`/offers/${body.id}/accept`, { signedName: body.signedName, acks: body.acks }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      qc.invalidateQueries({ queryKey: ['student', 'dashboard'] });
    },
  });
}
export function useUndoAcceptOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ offer: OfferListItem }>(`/offers/${id}/undo`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      qc.invalidateQueries({ queryKey: ['student', 'dashboard'] });
    },
  });
}
export function useDeclineOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; reason: 'better_offer' | 'higher_studies' | 'family' | 'other'; note?: string }) =>
      apiPost<{ offer: OfferListItem }>(`/offers/${body.id}/decline`, { reason: body.reason, note: body.note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      qc.invalidateQueries({ queryKey: ['student', 'dashboard'] });
    },
  });
}

export function useReleaseOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/offers/${id}/release`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offers'] }),
  });
}

export function useCreateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      application: string;
      candidateNameOnLetter: string;
      ctcAmountInr: number;
      designation?: string;
      role?: string;
      joiningDate?: string;
      location?: string;
      letterUrl?: string;
      bond?: { months: number; amountInr?: number };
    }) => apiPost<{ offer: OfferListItem }>('/offers', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      qc.invalidateQueries({ queryKey: ['applications'] });
    },
  });
}

export interface UploadResult {
  url: string;
  path: string;
  size: number;
  contentType: string;
  isStub: boolean;
}
export function useUploadOfferLetter() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      // apiPost sets JSON headers; use fetch directly to keep multipart intact.
      const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
      const token = (await import('./api')).getAccessToken();
      const res = await fetch(`${base}/uploads/offer-letter`, {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`);
      }
      const json = (await res.json()) as { data: UploadResult };
      return json.data;
    },
  });
}

// Legacy alias kept for callers that still use `useUndoAccept` (renamed to useUndoAcceptOffer above).
export const useUndoAccept = useUndoAcceptOffer;

// -- Platform ops --------------------------------------------------

export interface ApprovalRow {
  _id: string;
  kind: 'institution' | 'company';
  institutionName?: string;
  companyName?: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
  status: string;
  createdAt: string;
}

export interface PlatformKpis {
  tenants: { total: number; active: number; suspended: number };
  users: { total: number };
  drives: { total: number };
  offers: { total: number; accepted: number };
  exams: { total: number };
  approvals: { pending: number; institutions: number; companies: number };
}
export function usePlatformKpis() {
  return useQuery({
    queryKey: ['platform', 'kpis'],
    queryFn: () => apiGet<PlatformKpis>('/platform/kpis'),
    staleTime: 15_000,
  });
}

export function usePlatformApprovals(status = 'pending') {
  return useQuery({
    queryKey: ['platform', 'approvals', status],
    queryFn: () => apiGet<{ approvals: ApprovalRow[] }>(`/platform/approvals?status=${status}`),
  });
}

/**
 * Single-approval fetch for /platform/approvals/:id. When we know the
 * kind (institution|company) from the row that opened the page we
 * pass it as a query param so the server hits ONE collection instead
 * of two — same DTO comes back either way.
 */
export function usePlatformApproval(id: string | undefined, kind?: 'institution' | 'company') {
  return useQuery({
    queryKey: ['platform', 'approval', id, kind ?? ''],
    enabled: !!id,
    queryFn: () => {
      const qs = kind ? `?kind=${kind}` : '';
      return apiGet<{ approval: ApprovalRow & { kind: 'institution' | 'company' } }>(
        `/platform/approvals/${id}${qs}`,
      );
    },
  });
}

export function useApproveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; kind: 'institution' | 'company'; plan?: string }) =>
      apiPost(`/platform/approvals/${input.id}/approve`, { kind: input.kind, plan: input.plan }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'approvals'] }),
  });
}

export function useRejectApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; kind: 'institution' | 'company'; reason: string }) =>
      apiPost(`/platform/approvals/${input.id}/reject`, { kind: input.kind, reason: input.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'approvals'] }),
  });
}

export interface TpoReportsResponse {
  totals: { students: number; placed: number; medianCtc: number; highestCtc: number; companiesVisited: number };
  byBranch: Array<{ code: string; students: number; optedOut: number; placed: number; medianCtc: number; highestCtc: number }>;
  byCompany: Array<{ name: string; offersIssued: number; offersAccepted: number; medianCtc: number }>;
  salarySpread: Array<{ band: string; count: number }>;
}
export function useTpoReports() {
  return useQuery({
    queryKey: ['tpo', 'reports'],
    queryFn: () => apiGet<TpoReportsResponse>('/tpo/reports'),
  });
}

export interface GroupTeamMember {
  _id: string;
  fullName: string;
  email?: string;
  role: string;
  scope: string;
  lastActive?: string;
  status: 'active' | 'invited' | 'idle';
}
export function useGroupTeam(opts?: { q?: string; role?: string }) {
  const params = new URLSearchParams();
  if (opts?.q) params.set('q', opts.q);
  if (opts?.role) params.set('role', opts.role);
  const qs = params.toString();
  return useQuery({
    queryKey: ['group', 'team', qs],
    queryFn: () => apiGet<{ members: GroupTeamMember[]; total: number; collegesWithoutTpo: string[] }>(`/group/team${qs ? `?${qs}` : ''}`),
  });
}
export function useInviteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { emails: string[]; role: string; college?: string }) => apiPost('/group/team/invite', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'team'] }),
  });
}

export function useProvisionTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: 'group' | 'university' | 'college' | 'company';
      registeredName: string;
      displayName: string;
      gstin?: string;
      pan?: string;
      dataRegion: string;
      colleges?: string[];
      plan?: { code: string; seats: number; termYears: number; startsAt: string; poNumber?: string; aiCap?: number };
      firstAdmin: { name: string; email: string };
    }) => apiPost('/platform/tenants', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

// Legacy `useTpoCompanies` + `useInviteCompany` hooks removed. They called
// `/tpo/companies*` endpoints that never existed server-side. Callers now use
// the same `/tpo/partners` list as `/group/partners`, via useQuery directly
// (see apps/web/src/pages/tpo/Partners.tsx).

export interface TpoStudentRow {
  _id: string;
  name: string;
  roll?: string;
  branch: string;
  cgpa: number;
  backlogs: number;
  status: 'placed' | 'active' | 'idle' | 'blocked';
  applications: number;
  applicationsSummary?: string;
  offer?: { partner?: string; ctc?: number };
  phone?: string;
  reason?: string;
}
export function useTpoStudents(opts?: { branch?: string; status?: string; q?: string; page?: number }) {
  const params = new URLSearchParams();
  if (opts?.branch) params.set('branch', opts.branch);
  if (opts?.status) params.set('status', opts.status);
  if (opts?.q) params.set('q', opts.q);
  if (opts?.page) params.set('page', String(opts.page));
  const qstr = params.toString();
  return useQuery({
    queryKey: ['tpo', 'students', qstr],
    queryFn: () => apiGet<{ students: TpoStudentRow[]; total: number; page: number; totalPages: number }>(`/tpo/students${qstr ? `?${qstr}` : ''}`),
  });
}

export interface PlatformTenantRow {
  _id: string;
  name: string;
  slug: string;
  tenantType: string;
  status: string;
  subscription?: { planName?: string; aiCap?: number; aiSpentThisMonth?: number };
  users?: number;
  colleges?: number;
}
export function usePlatformTenants() {
  return useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiGet<{ tenants: PlatformTenantRow[] }>('/platform/tenants'),
  });
}

export interface PlatformTenantDetail {
  tenant: {
    _id: string;
    slug: string;
    name: string;
    tenantType: string;
    status: string;
    dataRegion?: string;
    address?: { country?: string; city?: string; state?: string };
    aiCap?: { monthlyBudgetInr?: number; spentThisMonth?: number };
    createdAt?: string;
    updatedAt?: string;
    dpdpConsentVersion?: string;
    settings?: Record<string, unknown>;
  };
  subscription: {
    _id?: string;
    plan?: { name?: string; code?: string; price?: { amount?: number; unit?: string } };
    status?: string;
    seats?: { allowed?: number; used?: number };
    term?: { startsAt?: string; endsAt?: string };
    aiCapOverride?: number;
    billing?: { gstin?: string; pan?: string };
    po?: { number?: string };
  } | null;
}

export function usePlatformTenant(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['platform', 'tenants', id],
    queryFn: () => apiGet<PlatformTenantDetail>(`/platform/tenants/${id}`),
  });
}

export interface TenantHealthSummary {
  tenantId: string;
  slug?: string;
  name?: string;
  tenantType?: string;
  status?: string;
  createdAt?: string;
  lastActivityAt: string | null;
  counts: {
    users: number;
    activeMemberships: number;
    colleges: number;
    drives: number;
    drivesLast30d: number;
    applications: number;
    applicationsLast30d: number;
    offersAccepted: number;
    offersAcceptedLast30d: number;
  };
  onboardingSignals: {
    hasUsers: boolean;
    hasEnoughUsers: boolean;
    hasColleges: boolean;
    hasAnyDrive: boolean;
    hasAnyOfferAccepted: boolean;
    activeLast30d: boolean;
  };
}
export interface TenantAiUsage {
  tenantId: string;
  slug?: string;
  displayName?: string;
  window: 'this_month' | '7d' | '30d' | '90d';
  since: string;
  aiCap: {
    monthlyBudgetInr: number;
    spentThisMonth: number;
    remainingInr: number;
    resetsOn: string | null;
  };
  breakdown: { drafted: number; sent: number; discarded: number };
  topActors: Array<{ userId: string; name?: string; email?: string; count: number }>;
  daily: Array<{ day: string; count: number }>;
}
export interface TenantSubscriptionHistory {
  tenantId: string;
  subscription: {
    _id: string;
    status?: string;
    seats?: { allowed?: number; used?: number };
    term?: { startsAt?: string; endsAt?: string };
    po?: { number?: string; issuedAt?: string };
    billing?: { gstin?: string; pan?: string; invoiceEmails?: string[] };
    aiCapOverride?: number;
    plan?: {
      _id: string;
      name?: string;
      code?: string;
      price?: { amount?: number | null; unit?: string; currency?: string };
      limits?: { aiMonthlyBudgetInr?: number };
    };
  } | null;
  invoices: Array<{
    _id: string;
    invoiceNumber?: string;
    issuedAt?: string;
    dueAt?: string;
    status?: string;
    totalInr?: number;
  }>;
  invoicesSummary: {
    total: number;
    byStatus: Record<string, { count: number; amountInr: number }>;
    byMonth: Array<{ month: string; count: number; amountInr: number }>;
  };
  timeline: Array<{
    _id: string;
    at: string;
    action: string;
    actor?: { userId?: string; role?: string };
    subject?: { kind?: string; id?: string };
    meta?: Record<string, unknown>;
  }>;
}
export function useTenantSubscriptionHistory(id: string | undefined, enabled = true) {
  return useQuery({
    enabled: Boolean(id) && enabled,
    queryKey: ['platform', 'tenants', id, 'subscription-history'],
    queryFn: () => apiGet<TenantSubscriptionHistory>(`/platform/tenants/${id}/subscription-history`),
  });
}

export function useTenantAiUsage(id: string | undefined, window: TenantAiUsage['window'] = '30d', enabled = true) {
  return useQuery({
    enabled: Boolean(id) && enabled,
    queryKey: ['platform', 'tenants', id, 'ai-usage', window],
    queryFn: () => apiGet<TenantAiUsage>(`/platform/tenants/${id}/ai-usage?window=${window}`),
  });
}

export function useTenantHealthSummary(id: string | undefined, enabled = true) {
  return useQuery({
    enabled: Boolean(id) && enabled,
    queryKey: ['platform', 'tenants', id, 'health-summary'],
    queryFn: () => apiGet<TenantHealthSummary>(`/platform/tenants/${id}/health-summary`),
  });
}

export interface EligibilitySummary {
  totalActive: number;
  eligible: number;
  blockers: Array<{ code: string; count: number; sampleMessage: string }>;
}
export function useMyEligibilitySummary() {
  return useQuery({
    queryKey: ['students', 'me', 'eligibility-summary'],
    queryFn: () => apiGet<EligibilitySummary>('/students/me/eligibility-summary'),
  });
}

export interface EligibleDriveRow {
  _id: string;
  driveName?: string;
  driveDate?: string;
  status?: string;
  college?: { _id: string; name?: string } | string;
  opportunity?: {
    _id?: string;
    title?: string;
    partner?: { name?: string; logoUrl?: string } | string;
    type?: string;
    compensation?: { ctc?: { min?: number; max?: number } };
  };
  applied?: boolean;
  eligibility?: {
    branches?: string[];
    minCGPA?: number;
    maxBacklogs?: number;
    graduationYears?: number[];
  };
}
export interface EligibleDrivesResponse {
  drives: EligibleDriveRow[];
  summary: { total: number; appliedTo: number; eligibleUnapplied: number };
  meta?: { page: number; limit: number; total: number };
}
export interface GroupAnalyticsTrends {
  tenantId: string;
  window: '7d' | '30d' | '90d';
  since: string;
  applications: Array<{ day: string; count: number }>;
  offersAccepted: Array<{ day: string; count: number }>;
  interviews: Array<{ day: string; count: number }>;
}
export type MyTimelineKind = 'application' | 'interview' | 'offer' | 'badge' | 'attempt' | 'notification';
export interface MyTimelineEvent {
  kind: MyTimelineKind;
  at: string;
  title: string;
  detail?: string;
  refId: string;
  refKind: string;
  meta?: Record<string, unknown>;
}
export interface MyTimelineParams {
  page?: number;
  limit?: number;
  kind?: MyTimelineKind | '';
  from?: string;
  to?: string;
  sortDir?: 'asc' | 'desc';
}
export function useMyTimeline(params: MyTimelineParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['students', 'me', 'timeline', params],
    queryFn: () => apiGet<{ events: MyTimelineEvent[]; meta?: { page: number; limit: number; total: number } }>(
      `/students/me/timeline${qs.toString() ? `?${qs}` : ''}`,
    ),
  });
}

export interface PlatformKpisTrends {
  window: '7d' | '30d' | '90d';
  since: string;
  applications: Array<{ day: string; count: number }>;
  offersAccepted: Array<{ day: string; count: number }>;
  drivesCreated: Array<{ day: string; count: number }>;
  tenantsCreated: Array<{ day: string; count: number }>;
  usersCreated: Array<{ day: string; count: number }>;
}
export function usePlatformKpisTrends(window: '7d' | '30d' | '90d' = '30d') {
  return useQuery({
    queryKey: ['platform', 'kpis', 'trends', window],
    queryFn: () => apiGet<PlatformKpisTrends>(`/platform/kpis/trends?window=${window}`),
  });
}

export interface WorkflowRunsSummary {
  workflowId: string;
  workflowName?: string;
  totals: { runs: number };
  stages: Record<string, number>;
  dwell: Record<string, { avgSec: number; sampledCount: number }>;
  revenue: Record<string, { count: number; amountInr: number; partnerInr: number; kollezInr: number }>;
  topTenants: Array<{ tenantId: string; name?: string; slug?: string; count: number }>;
}
export function useWorkflowRunsSummary(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['workflows', id, 'runs-summary'],
    queryFn: () => apiGet<WorkflowRunsSummary>(`/workflows/${id}/runs-summary`),
  });
}

export interface InterviewCandidatePreview {
  interviewId: string;
  candidate: {
    userId: string;
    name?: string;
    email?: string;
    phone?: string;
    academic?: {
      program?: string;
      specialization?: string;
      branchCode?: string;
      cgpa?: number;
      backlogs?: number;
      expectedGraduationYear?: number;
    } | null;
    skills?: Array<{ name?: string; slug?: string; level?: string } | string>;
    projects?: Array<{ title?: string; description?: string; techStack?: string[] }>;
    certifications?: Array<{ name?: string; issuer?: string; year?: number }>;
    placementStatus?: { isPlaced?: boolean; sitOut?: boolean } | null;
  };
  application: {
    id?: string;
    status?: string;
    appliedAt?: string;
    matchScore?: { overall?: number; reasoning?: string } | null;
  } | null;
  resume: {
    name?: string;
    atsScore?: number | null;
    summary?: string;
  } | null;
  rounds: Array<{
    _id: string;
    roundNumber?: number;
    roundType?: string;
    scheduledAt?: string;
    status?: string;
    result?: string;
  }>;
}
export function useInterviewCandidatePreview(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['interviews', id, 'candidates-preview'],
    queryFn: () => apiGet<InterviewCandidatePreview>(`/interviews/${id}/candidates-preview`),
  });
}

export function useGroupAnalyticsTrends(window: '7d' | '30d' | '90d' = '30d') {
  return useQuery({
    queryKey: ['group', 'analytics', 'trends', window],
    queryFn: () => apiGet<GroupAnalyticsTrends>(`/group/analytics/trends?window=${window}`),
  });
}

export interface TpoDashboardKpis {
  drivesTodayCount: number;
  drivesThisWeekCount: number;
  pendingSignoffCount: number;
  interviewsTodayCount: number;
  applicationsLast7d: number;
  acceptedOffersLifetime: number;
  placementRatePct: number;
  totalStudents: number;
}
export interface TpoDashboardResponse {
  kpis: TpoDashboardKpis;
  drivesToday: unknown[];
  drivesThisWeek: unknown[];
  pendingSignoffs: unknown[];
  interviewsToday: unknown[];
}
export function useTpoDashboard() {
  return useQuery({
    queryKey: ['tpo', 'dashboard'],
    queryFn: () => apiGet<TpoDashboardResponse>('/tpo/dashboard'),
  });
}

export interface CompanyDashboardKpis {
  openingsByStatus: Record<string, number>;
  applicationsLast7d: number;
  upcomingInterviewsCount: number;
  offersPendingCount: number;
  offersAcceptedLifetime: number;
  activeMous: number;
  fundedByAcceptedCTC: number;
}
export interface CompanyDashboardResponse {
  kpis: CompanyDashboardKpis;
  activeOpenings: Array<{
    _id: string;
    title?: string;
    type?: string;
    applicationCount: number;
    publishedAt?: string;
    applicationSettings?: { closesAt?: string };
  }>;
  upcomingInterviews: unknown[];
  offersPending: unknown[];
}
export function useCompanyDashboard() {
  return useQuery({
    queryKey: ['company', 'dashboard'],
    queryFn: () => apiGet<CompanyDashboardResponse>('/company/dashboard'),
  });
}

export interface RecentAutoApprovedMembership {
  membershipId: string;
  userId: string;
  fullName?: string;
  email?: string;
  phone?: string;
  verifiedAt?: string;
  relationship?: string;
}
export interface RecentAutoApprovedResponse {
  candidates: RecentAutoApprovedMembership[];
  windowDays: number;
  meta?: { page: number; limit: number; total: number };
}
export function useRecentAutoApprovedMemberships(opts: { q?: string; relationship?: string; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (opts.q) qs.set('q', opts.q);
  if (opts.relationship) qs.set('relationship', opts.relationship);
  if (opts.limit) qs.set('limit', String(opts.limit));
  qs.set('sortBy', 'verifiedAt');
  qs.set('sortDir', 'desc');
  return useQuery({
    queryKey: ['tpo', 'memberships', 'recently-auto-approved', opts],
    queryFn: () => apiGet<RecentAutoApprovedResponse>(`/tpo/memberships/recently-auto-approved?${qs.toString()}`),
  });
}
export function useRevokeMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { membershipId: string; reason?: 'bad_auto_approval' | 'mistaken_claim' | 'other'; note?: string }) =>
      apiPost<{ membershipId: string; status: string; alreadyEnded?: boolean }>(
        `/tpo/memberships/${v.membershipId}/revoke`,
        { reason: v.reason ?? 'bad_auto_approval', note: v.note },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tpo', 'memberships'] }),
  });
}

export interface TenantDomain {
  domain: string;
  status: 'pending' | 'verified' | 'failed';
  token: string;
  addedBy?: string;
  addedAt: string;
  verifiedAt?: string;
  lastCheckedAt?: string;
  checkAttempts: number;
}
export function useTenantDomains() {
  return useQuery({
    queryKey: ['tpo', 'tenant', 'domains'],
    queryFn: () => apiGet<{ domains: TenantDomain[] }>('/tpo/tenant/domains'),
  });
}
export function useAddTenantDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { domain: string }) =>
      apiPost<{ domain: string; token: string; txtRecordName: string; txtRecordValue: string; status: string }>(
        '/tpo/tenant/domains',
        v,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tpo', 'tenant', 'domains'] }),
  });
}
export function useVerifyTenantDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { domain: string }) =>
      apiPost<{ status: string; verifiedAt?: string; checkAttempts: number; reason?: string }>(
        `/tpo/tenant/domains/${encodeURIComponent(v.domain)}/verify`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tpo', 'tenant', 'domains'] }),
  });
}
export function useRemoveTenantDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { domain: string }) =>
      apiDelete<{ removed: boolean }>(`/tpo/tenant/domains/${encodeURIComponent(v.domain)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tpo', 'tenant', 'domains'] }),
  });
}

export function useMyEligibleDrives(opts: { limit?: number; includeApplied?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.includeApplied) qs.set('includeApplied', 'true');
  return useQuery({
    queryKey: ['students', 'me', 'eligible-drives', opts],
    queryFn: () => apiGet<EligibleDrivesResponse>(`/students/me/eligible-drives${qs.toString() ? `?${qs}` : ''}`),
  });
}
export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; typedConfirmation: string; reason?: string }) =>
      apiPost(`/platform/tenants/${input.id}/suspend`, { typedConfirmation: input.typedConfirmation, reason: input.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

// -- Nexie --------------------------------------------------------

export interface ParsedJd {
  company?: string;
  role?: string;
  ctcInr?: number;
  location?: string;
  minCGPA?: number;
  maxBacklogs?: number;
  eligibleBranches?: string[];
  requiredSkills?: string[];
  driveDate?: string;
  bond?: { months?: number; amountInr?: number };
  workMode?: 'On-Site' | 'Remote' | 'Hybrid';
  concerns?: string[];
}

export function useParseJd() {
  return useMutation({
    mutationFn: (text: string) => apiPost<{ parsed: ParsedJd }>('/nexie/parse-jd', { text }),
  });
}

export interface DraftItem {
  _id: string;
  kind: string;
  status: 'draft' | 'edited' | 'discarded' | 'sent';
  payload: unknown;
  citations: unknown[];
  createdAt: string;
}

export interface NexieAuditRow {
  _id: string;
  at: string;
  action: string;
  actor: { userId?: string; role?: string };
  subject?: { kind?: string; id?: string };
  meta?: Record<string, unknown>;
}

export function useNexieAudit(limit = 50) {
  return useQuery({
    queryKey: ['nexie', 'audit', limit],
    queryFn: () => apiGet<{ rows: NexieAuditRow[]; summary: { draftCount: number; sentCount: number; count: number } }>(`/nexie/audit?limit=${limit}`),
  });
}

export function useNexieDrafts(status = 'draft') {
  return useQuery({
    queryKey: ['nexie', 'drafts', status],
    queryFn: () => apiGet<{ drafts: DraftItem[] }>(`/nexie/drafts?status=${status}`),
  });
}

export function useCreateNexieDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/nexie/draft', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nexie', 'drafts'] }),
  });
}

export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      apiPost(`/nexie/drafts/${input.id}/discard`, { reason: input.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nexie', 'drafts'] }),
  });
}

export interface MatchExplainResult {
  narrative: string;
  bullets: string[];
  concerns: string[];
  /**
   * `href` is optional (Step 41). Chips render as in-app links when set; the React Router's
   * route guards decide whether the destination is visible to the current viewer.
   */
  citations: Array<{ sourceType: string; sourceId?: string; label: string; snippet?: string; href?: string }>;
}

export interface ApplicationDetail extends ApplicationListItem {
  student?: {
    _id: string;
    fullName?: string;
    email?: string;
    phone?: string;
    rollNumber?: string;
    branch?: string;
    cgpa?: number;
    class10Marks?: number;
    class12Marks?: number;
    backlogs?: number;
    skills?: string[];
    resumeUrl?: string;
    resumeText?: string;
    mentor?: { fullName: string };
  };
  // Application refs the specific role of the drive it applied to. Value is an ObjectId
  // (string) — populate happens on the Application's opportunity when needed.
  opportunity?: string;
  drive?: {
    _id?: string;
    driveName?: string;
    driveDate?: string;
    tier?: 0 | 1 | 2;
    partner?: { name: string };
    // Legacy: single opportunity used when the drive only hosts one role. Preserved on the
    // API response as a computed alias for `roles[0].opportunity` so the read path doesn't
    // break during the multi-role rollout.
    opportunity?: {
      title: string;
      compensation?: { ctc?: { min?: number } };
      eligibility?: { branches?: string[]; minCGPA?: number; maxBacklogs?: number };
    };
    // Multi-role shape — one entry per Opportunity hosted at this drive.
    roles?: Array<{
      _id?: string;
      opportunity?: {
        _id: string;
        title: string;
        compensation?: { ctc?: { min?: number } };
        eligibility?: { branches?: string[]; minCGPA?: number; maxBacklogs?: number };
      };
    }>;
  };
  matchScore?: {
    overall: number;
    // F-088: the API writes `breakdown` as an object keyed by dimension name (via matchScore.ts).
    // Older code assumed an array shape — allow both here so consumers don't crash on either.
    breakdown: Array<{ dimension: string; score: number }> | Record<string, number>;
    reasoning?: string;
  };
  notes?: Array<{ _id: string; author: string; at: string; text: string }>;
  statusHistory?: Array<{ status: string; at: string; by?: string; note?: string }>;
}
export function useApplication(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['application', id],
    queryFn: () => apiGet<{ application: ApplicationDetail; siblings?: { prev?: string; next?: string; index: number; total: number } }>(`/applications/${id}`),
  });
}
export function useAddApplicationNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => apiPost<{ application: ApplicationDetail }>(`/applications/${id}/notes`, { text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['application', id] }),
  });
}

export function useMatchExplain() {
  return useMutation({
    mutationFn: (applicationId: string) =>
      apiPost<{ explain: MatchExplainResult }>('/nexie/match-explain', { applicationId }),
  });
}

export interface WeeklyBriefStats {
  drivesRun: number;
  applications: number;
  offersReleased: number;
  offersAccepted: number;
  medianCtcInr: number;
  highestCtcInr: number;
  byDepartment: Array<{ departmentCode: string; departmentName: string; applications: number; offersAccepted: number }>;
  topDrivesThisWeek: Array<{ driveName: string; company: string; offered: number }>;
}
export interface WeeklyBriefResponse {
  brief: {
    stats: WeeklyBriefStats;
    narrative: string;
    bullets: string[];
    suggestedNextSteps: string[];
    citations: Array<{ sourceType: string; label: string; snippet?: string }>;
  };
  draftId: string;
}

export function useWeeklyBrief() {
  return useMutation({
    mutationFn: (body: { collegeId: string; weekStart?: string; weekEnd?: string }) =>
      apiPost<WeeklyBriefResponse>('/nexie/weekly-brief', body),
  });
}

export function useSendDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      draftId: string;
      channels?: Array<'in_app' | 'email' | 'sms' | 'whatsapp'>;
      extraRecipientUserIds?: string[];
      extraRecipientEmails?: string[];
      extraRecipientPhones?: string[];
    }) => apiPost<{ draft: { _id: string; status: string; sentAt: string }; recipientCount: number; notificationCount: number }>('/messages/send', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nexie', 'drafts'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// -- Assessments (P3 first slice) ---------------------------------

export interface QuestionBankRow {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  categories: string[];
  tags: string[];
  status: 'active' | 'archived';
  questionCount: number;
  createdAt: string;
}
export function useQuestionBanks() {
  return useQuery({
    queryKey: ['assessments', 'banks'],
    queryFn: () => apiGet<{ banks: QuestionBankRow[] }>('/assessments/banks'),
  });
}
export function useCreateQuestionBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; categories?: string[]; tags?: string[] }) =>
      apiPost<{ bank: QuestionBankRow }>('/assessments/banks', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', 'banks'] }),
  });
}

export interface QuestionRow {
  _id: string;
  stem: string;
  type: string;
  difficulty: string;
  marks: number;
  status: 'draft' | 'review' | 'approved' | 'retired';
  source: 'manual' | 'ai';
  skill?: string;
  options?: Array<{ text: string; correct?: boolean }>;
  explanation?: string;
  quality?: { flags?: string[] };
  createdAt: string;
}
export function useBankQuestions(bankId: string | undefined) {
  return useQuery({
    queryKey: ['assessments', 'questions', bankId],
    enabled: !!bankId,
    queryFn: () => apiGet<{ questions: QuestionRow[] }>(`/assessments/questions?bank=${bankId}`),
  });
}
export function useApproveQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/assessments/questions/${id}/approve`),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ['assessments', 'questions'] });
    },
  });
}
export function useGenerateQuestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      bankId: string;
      topic: string;
      count: number;
      types: Array<'single' | 'multi' | 'tf' | 'blank' | 'num' | 'short' | 'long'>;
      levelMix?: { easy?: number; medium?: number; hard?: number };
      skill?: string;
    }) => apiPost<{ draftedCount: number; draftedIds: string[]; raw: unknown[] }>('/nexie/generate-questions', body),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['assessments', 'questions', vars.bankId] });
      qc.invalidateQueries({ queryKey: ['assessments', 'banks'] });
    },
  });
}

// -- Notifications ------------------------------------------------

export interface NotificationRow {
  _id: string;
  type: string;
  title: string;
  body: string;
  action?: { cta: string; url: string };
  readAt?: string;
  createdAt: string;
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiGet<{ notifications: NotificationRow[]; unread: number }>('/notifications'),
    refetchInterval: 30_000,
    // Don't retry on 401 — auth may still be hydrating; the next mount fires the fetch again.
    retry: (failureCount, err) => {
      if ((err as { response?: { status?: number } })?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/notifications/${id}/mark-read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/**
 * Step 60: inverse of mark-read. Un-reads a single notification so it re-appears in the
 * bell count + counts-by-subject queries. Server sets $unset readAt and drops status back
 * to 'Sent'. Same cache invalidation as mark-read.
 */
export function useMarkNotificationUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/notifications/${id}/mark-unread`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/**
 * Step 51: batch unread counts for a list of subject ids of one kind. Used by list pages
 * (Applications today; Offers/Drives when they need it) to render an unread dot per row.
 * Sorts + joins the ids into a stable cache key so identical mounts share one call.
 */
export function useNotifCountsBySubject(
  kind: 'Application' | 'Offer' | 'Drive' | 'Badge' | 'WorkflowRun' | 'Interview',
  ids: string[] | null | undefined,
) {
  const stableIds = (ids ?? []).slice().sort();
  const key = stableIds.join(',');
  return useQuery({
    queryKey: ['notifications', 'counts-by-subject', kind, key],
    queryFn: () =>
      apiPost<{ counts: Record<string, number> }>('/notifications/counts-by-subject', {
        kind,
        ids: stableIds,
      }),
    enabled: stableIds.length > 0,
    // Same cadence as /notifications so the shell bell + list dots stay in agreement.
    refetchInterval: 30_000,
  });
}

/**
 * Step 52: mark every unread notification for (kind, id) as read. Called when the student
 * "opens" the thing — e.g. clicks an application row → drawer opens. Invalidates both the
 * shell feed and the per-subject counts so the bell + row dots update together.
 */
export function useMarkNotifReadBySubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: 'Application' | 'Offer' | 'Drive' | 'Badge' | 'WorkflowRun' | 'Interview';
      id: string;
    }) => apiPost<{ updated: number }>('/notifications/mark-read-by-subject', input),
    onSuccess: () => {
      // Invalidate the shell feed AND every counts-by-subject query. Both keys start with
      // 'notifications', so a single top-level invalidation cascades.
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// -- Calendar (unified feed) --------------------------------------

export interface CalendarEvent {
  id: string;
  kind: 'drive' | 'interview' | 'holiday';
  title: string;
  subtitle?: string;
  startAt: string;
  endAt?: string;
  room?: string;
  tone?: 'info' | 'warning' | 'success' | 'danger' | 'default';
}

export function useCalendar(opts: { from: string; to: string; scope?: 'tpo' | 'student' | 'company' | 'principal' }) {
  return useQuery({
    queryKey: ['calendar', opts.from, opts.to, opts.scope ?? 'tpo'],
    queryFn: () =>
      apiGet<{ events: CalendarEvent[] }>(
        `/calendar?from=${encodeURIComponent(opts.from)}&to=${encodeURIComponent(opts.to)}&scope=${opts.scope ?? 'tpo'}`,
      ),
  });
}

// -- Opportunities (company depth) --------------------------------

export interface OpportunityRow {
  _id: string;
  title: string;
  slug: string;
  type: string;
  status: 'Draft' | 'Active' | 'Paused' | 'Closed' | 'Archived';
  scope: 'GLOBAL' | 'TENANT' | 'COLLEGE_LIST';
  publishedAt?: string;
  closedAt?: string;
  applicationCount: number;
  compensation?: {
    ctc?: { min?: number; max?: number; currency?: 'INR' | 'USD' };
    stipend?: { amount?: number; unit?: 'per_month' | 'total'; currency?: 'INR' | 'USD' };
    courseFee?: number;
    prizes?: Array<{ rank: number; amount: number; description?: string; currency?: 'INR' | 'USD' }>;
  };
  duration?: {
    months?: number;
    hours?: number;
    days?: number;
    weeks?: number;
    startsAt?: string;
    endsAt?: string;
  };
  workMode?: 'onsite' | 'remote' | 'hybrid';
  audience?: { alumni?: boolean };
  locations?: Array<{ city: string; state?: string; country?: string; isRemote?: boolean }>;
  requiredSkills?: string[];
  preferredSkills?: string[];
  eligibility?: { minCGPA?: number; maxBacklogs?: number; eligibleBranches?: string[] };
  description?: string;
  responsibilities?: string;
  benefits?: string;
  /** Populated partner sub-doc when the API includes it. */
  partner?: { _id: string; name: string; logoUrl?: string };
}

/**
 * useOpportunities — Pass 4 canonical.
 * Server: /opportunities with page/limit/q/status/partner/type/scope/workMode/
 *   ctcMin/ctcMax/postedAfter/postedBefore/sortBy/sortDir. Returns:
 *   `{opportunities, summary.byStatus} + {page,limit,total}`.
 */
export interface OpportunitiesListOpts {
  page?: number;
  limit?: number;
  q?: string;
  status?: string;
  partner?: string;
  type?: string;
  scope?: 'GLOBAL' | 'TENANT' | 'COLLEGE_LIST';
  workMode?: string;
  ctcMin?: number;
  ctcMax?: number;
  postedAfter?: string;
  postedBefore?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'applicationDeadline' | 'compensation.ctc.max';
  sortDir?: 'asc' | 'desc';
}
export interface OpportunitiesListResponse {
  opportunities: OpportunityRow[];
  summary?: { byStatus?: Record<string, number> };
}
export function useOpportunities(opts?: OpportunitiesListOpts) {
  const scope = useKeyScope();
  const qs = new URLSearchParams();
  if (opts?.page) qs.set('page', String(opts.page));
  if (opts?.limit) qs.set('limit', String(opts.limit));
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.partner) qs.set('partner', opts.partner);
  if (opts?.type) qs.set('type', opts.type);
  if (opts?.scope) qs.set('scope', opts.scope);
  if (opts?.workMode) qs.set('workMode', opts.workMode);
  if (typeof opts?.ctcMin === 'number') qs.set('ctcMin', String(opts.ctcMin));
  if (typeof opts?.ctcMax === 'number') qs.set('ctcMax', String(opts.ctcMax));
  if (opts?.postedAfter) qs.set('postedAfter', opts.postedAfter);
  if (opts?.postedBefore) qs.set('postedBefore', opts.postedBefore);
  if (opts?.sortBy) qs.set('sortBy', opts.sortBy);
  if (opts?.sortDir) qs.set('sortDir', opts.sortDir);
  const q = qs.toString();
  return useQuery({
    queryKey: ['opportunities', scope, q],
    queryFn: () => apiGet<OpportunitiesListResponse>(`/opportunities${q ? `?${q}` : ''}`),
  });
}

// GET /opportunities/:id/eligibility — Pass 2 deterministic student eligibility precheck.
export interface OpportunityEligibilityReason {
  code:
    | 'not_active' | 'closed' | 'no_profile'
    | 'cgpa_below_min' | 'too_many_backlogs' | 'branch_ineligible' | 'year_ineligible'
    | 'already_placed' | 'sit_out' | 'already_applied';
  message: string;
  required?: unknown;
  actual?: unknown;
}
export function useOpportunityEligibility(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['opportunity-eligibility', id],
    queryFn: () => apiGet<{ eligible: boolean; reasons: OpportunityEligibilityReason[] }>(
      `/opportunities/${id}/eligibility`,
    ),
  });
}

// GET /opportunities/:id/similar — Pass 8 related-openings list.
export function useSimilarOpportunities(id: string | undefined, limit = 6) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['opportunity-similar', id, limit],
    queryFn: () => apiGet<{ sourceId: string; items: OpportunityRow[] }>(
      `/opportunities/${id}/similar?limit=${limit}`,
    ),
  });
}

// F-103: TPO's PostDrive form needs to pick a Partner + Opportunity. Reuse /group/partners.
export interface GroupPartnerRow {
  _id: string;
  name: string;
  slug?: string;
  logo?: string;
  status?: string;
  type?: string;
}
export function useGroupPartnersForPicker() {
  return useQuery({
    queryKey: ['group', 'partners', 'picker'],
    queryFn: () => apiGet<{ rows: GroupPartnerRow[] }>('/group/partners?limit=100'),
  });
}

export function useOpportunity(id: string | undefined) {
  const scope = useKeyScope();
  return useQuery({
    queryKey: ['opportunities', scope, id],
    enabled: !!id,
    queryFn: () => apiGet<{ opportunity: OpportunityRow }>(`/opportunities/${id}`),
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<{ opportunity: OpportunityRow }>('/opportunities', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
  });
}

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: Record<string, unknown> }) =>
      apiPatch<{ opportunity: OpportunityRow }>(`/opportunities/${input.id}`, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
  });
}

export function usePublishOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/opportunities/${id}/publish`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
  });
}

export interface PartnerCollegeRow {
  _id: string;
  name: string;
  code: string;
  type: string;
  totalStudents?: number;
  mouActive: boolean;
  daysToMouExpiry: number | null;
  offersReleased: number;
  offersAccepted: number;
}
export interface Profile360Dim {
  key: string;
  label: string;
  weight: number;
  score: number;
  weightedScore: number;
  evidence: string[];
  confidence: 'hi' | 'lo';
}
export interface Profile360Result {
  overall: number;
  dimensions: Profile360Dim[];
  computedAt: string;
  updates: string[];
}
export function useProfile360() {
  return useQuery({
    queryKey: ['student', 'profile-360'],
    queryFn: () => apiGet<{ readiness: Profile360Result }>('/students/me/profile-360'),
  });
}

// ------------------------------------------------------------
// /students/me/dashboard — Pass 14 composite. Single-call home page fetch.
// ------------------------------------------------------------
export interface MeDashboardResponse {
  kpis: {
    applicationsByStatus: Record<string, number>;
    offersByStatus: Record<string, number>;
    upcomingInterviewCount: number;
    pendingOfferCount: number;
    unreadNotifications: number;
    activeBadges: number;
  };
  upcomingInterviews: Array<{
    _id: string;
    scheduledAt: string;
    endAt?: string;
    roundType?: string;
    roundNumber?: number;
    room?: string;
    status: string;
    drive?: { _id: string; driveName?: string } | null;
  }>;
  pendingOffers: Array<{
    _id: string;
    status: string;
    deadlineAt?: string;
    undoWindowEndsAt?: string;
    ctc?: { amountInr?: number };
    partner?: { _id: string; name?: string; slug?: string } | null;
    role?: string;
    designation?: string;
  }>;
  recentApplications: Array<{
    _id: string;
    status: string;
    appliedAt?: string;
    drive?: { _id: string; driveName?: string; driveDate?: string } | null;
    matchScore?: { overall?: number };
  }>;
  eligibleDrivesPreview: Array<{
    _id: string;
    driveName?: string;
    driveDate?: string;
    college?: string | { name?: string; code?: string };
    partner?: { name?: string; slug?: string } | null;
  }>;
  lastActivity: string | null;
}

export function useMeDashboard() {
  return useQuery({
    queryKey: ['student', 'dashboard'],
    queryFn: () => apiGet<MeDashboardResponse>('/students/me/dashboard'),
    // Home page is a first-paint fetch; a 30s stale window keeps the UI feeling
    // live without hammering the composite endpoint.
    staleTime: 30_000,
  });
}

export function useProfile360Explain() {
  return useMutation({
    mutationFn: () =>
      apiPost<{ readiness: Profile360Result; narrative: string; byDimension: Array<{ key: string; note: string }> }>(
        '/students/me/profile-360/explain',
      ),
  });
}

export function usePartnerColleges() {
  return useQuery({
    queryKey: ['company', 'colleges'],
    queryFn: () => apiGet<{ colleges: PartnerCollegeRow[] }>('/company/colleges'),
  });
}

export interface PartnerSkillDemand {
  partnerId: string;
  totalOpportunities: number;
  requiredSkills: Array<{ skill: string; count: number }>;
  preferredSkills: Array<{ skill: string; count: number }>;
}
export interface PartnerPipeline {
  partnerId: string;
  opportunities: Record<string, number>;
  applicationsByStatus: Record<string, number>;
  offersByStatus: Record<string, number>;
  medianCTCInr: number | null;
}
export function usePartnerPipeline(partnerId: string | undefined) {
  return useQuery({
    enabled: Boolean(partnerId),
    queryKey: ['partner-pipeline', partnerId],
    queryFn: () => apiGet<PartnerPipeline>(`/partners/${partnerId}/pipeline`),
  });
}

export interface PartnerCollegesMou {
  _id: string;
  tenant: { _id: string; slug?: string; displayName?: string } | string;
  status: string;
  kind?: string;
  term?: { startsAt?: string; endsAt?: string };
}
export function usePartnerCollegesByMou(partnerId: string | undefined) {
  return useQuery({
    enabled: Boolean(partnerId),
    queryKey: ['partner-colleges', partnerId],
    queryFn: () => apiGet<{ partnerId: string; colleges: PartnerCollegesMou[] }>(`/partners/${partnerId}/colleges`),
  });
}

export function usePartnerSkillDemand(partnerId: string | undefined, limit = 20) {
  return useQuery({
    enabled: Boolean(partnerId),
    queryKey: ['partner-skill-demand', partnerId, limit],
    queryFn: () => apiGet<PartnerSkillDemand>(`/partners/${partnerId}/skill-demand?limit=${limit}`),
  });
}

export interface DrivePanelsSummary {
  driveId: string;
  panelCount: number;
  panels: Array<{
    panel: string;
    total: number;
    scheduled: number;
    completed: number;
    noShow: number;
    cancelled: number;
    earliestSlot: string | null;
    latestSlot: string | null;
    avgDurationMinutes: number;
    interviewerCount: number;
  }>;
  summary: { totalInterviews: number; completed: number; scheduled: number };
}
export function useDrivePanelsSummary(driveId: string | undefined, enabled = true) {
  return useQuery({
    enabled: Boolean(driveId) && enabled,
    queryKey: ['drive', driveId, 'panels-summary'],
    queryFn: () => apiGet<DrivePanelsSummary>(`/drives/${driveId}/panels-summary`),
  });
}

export interface DriveFeedbackSummary {
  driveId: string;
  totalCount: number;
  completedCount: number;
  avgRating: number | null;
  ratingDistribution: Record<'1' | '2' | '3' | '4' | '5', number>;
  recommendationDistribution: Record<string, number>;
  resultDistribution: Record<string, number>;
  topStrengths: Array<{ tag: string; count: number }>;
  topWeaknesses: Array<{ tag: string; count: number }>;
  perPanel: Array<{ panel: string; avgRating: number | null; count: number }>;
}
export function useDriveFeedbackSummary(driveId: string | undefined, enabled = true) {
  return useQuery({
    enabled: Boolean(driveId) && enabled,
    queryKey: ['drive', driveId, 'feedback-summary'],
    queryFn: () => apiGet<DriveFeedbackSummary>(`/drives/${driveId}/feedback-summary`),
  });
}

// Step 93 · Company Team (M1 from Step 90 ledger). Recruiter admin sees every user
// on their Partner tenant — mirrors the Group Team page shape without the invite mutation
// (invite server-side ships in a follow-up P1).
export interface CompanyTeamMember {
  _id: string;
  fullName: string;
  email: string;
  role: string;
  createdAt?: string;
  lastLoginAt?: string;
}
export function useCompanyTeam(opts?: { q?: string; role?: string }) {
  const qs = new URLSearchParams();
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.role) qs.set('role', opts.role);
  const query = qs.toString();
  return useQuery({
    queryKey: ['company', 'team', opts?.q ?? '', opts?.role ?? ''],
    queryFn: () => apiGet<{ members: CompanyTeamMember[]; total: number; partner?: { _id: string; name: string } }>(
      `/company/team${query ? `?${query}` : ''}`,
    ),
  });
}

export function useCloseOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/opportunities/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
  });
}

// -- Group depth --------------------------------------------------

export interface GroupAnalyticsRow {
  _id: string;
  /** Alias for `_id` — populated when the group aggregation returns per-college rows. */
  collegeId?: string;
  name: string;
  code: string;
  type: string;
  drives: number;
  offersIssued: number;
  offersAccepted: number;
  avgCtc: number;
  maxCtc: number;
}
export function useGroupAnalytics() {
  return useQuery({
    queryKey: ['group', 'analytics'],
    queryFn: () => apiGet<{ colleges: GroupAnalyticsRow[]; totals: { colleges: number; drives: number; offersAccepted: number; applications: number } }>('/group/analytics'),
  });
}

export interface HodDeptResponse {
  department: { _id: string; name: string; code: string };
  readiness: { placed: number; ready: number; almost: number; none: number };
  totalStudents: number;
  placementRate: number;
  atRisk: Array<{ userId: string; name: string; roll?: string; reason: string }>;
  skillGaps: Array<{ skill: string; drivesAsking: number; studentsHaving: number; studentsHavingPct: number }>;
}
export function useHodDepartment() {
  return useQuery({
    queryKey: ['hod', 'department'],
    queryFn: () => apiGet<HodDeptResponse>('/hod/department'),
  });
}

export interface FacultyMentee {
  _id: string; name: string; email: string; phone?: string; roll?: string; branch?: string; cgpa: number; seg: 'attn' | 'going' | 'placed'; note: string;
}
export interface FacultyMeProfile {
  _id: string;
  user: string;
  tenant: string;
  employeeId?: string;
  designation: string;
  type: 'permanent' | 'adjunct' | 'visiting' | 'guest' | 'contract';
  dateOfJoining: string;
  dateOfLeaving?: string;
  phdStatus: 'holds_phd' | 'pursuing_phd' | 'not_pursuing';
  researchAreas: string[];
  qualifications: Array<{
    degree: 'PhD' | 'Master' | 'Bachelor' | 'Diploma' | 'Certificate';
    degreeName: string;
    specialization?: string;
    institution?: string;
    university?: string;
    year: number;
    class?: string;
    cgpaOrPercentage?: number;
  }>;
  subjectsTaught: Array<{
    subjectCode: string;
    subjectName: string;
    programmeCode?: string;
    specializationCode?: string;
    semester?: number;
    level: 'UG' | 'PG' | 'PhD' | 'Diploma';
    creditHours?: number;
    currentSemester: boolean;
  }>;
  roleAssignments: Array<{
    role: string;
    tenant: string;
    department?: string;
    from: string;
    to?: string;
    active: boolean;
    notes?: string;
  }>;
  experience?: {
    years?: number;
    previousInstitutions?: Array<{
      institutionName: string;
      designation?: string;
      from: string;
      to?: string;
      rolesHeld?: string[];
    }>;
  };
  publications?: {
    count?: number;
    citationCount?: number;
    hIndex?: number;
    orcidId?: string;
    googleScholarUrl?: string;
    booksAuthored?: number;
    patents?: number;
  };
  honours?: Array<{ title: string; year?: number; awardedBy?: string }>;
  contact?: {
    officialEmail?: string;
    personalEmail?: string;
    mobile?: string;
    extension?: string;
    cabin?: string;
  };
  status: 'active' | 'on_leave' | 'sabbatical' | 'retired' | 'terminated';
  department?: { _id: string; name: string; code?: string } | string;
  createdAt: string;
  updatedAt: string;
}
export function useFacultyMe() {
  return useQuery({
    queryKey: ['faculty', 'me'],
    queryFn: () => apiGet<FacultyMeProfile | null>('/faculty/me'),
  });
}
export function useFacultyMentees() {
  return useQuery({
    queryKey: ['faculty', 'mentees'],
    queryFn: () => apiGet<{ mentees: FacultyMentee[] }>('/faculty/mentees'),
  });
}

export interface PrincipalOverviewResponse {
  departments: Array<{ code: string; name: string; total: number; placed: number; pct: number }>;
  totals: { students: number; placed: number };
  college?: { _id: string; name?: string };
}
export interface CheckIn {
  _id: string;
  format: 'person' | 'call' | 'whatsapp' | 'video' | 'email';
  at: string;
  text: string;
  riskFlags: string[];
  flaggedToTpo: boolean;
  suggestedNextStep?: string;
  facultyUserId?: { fullName?: string };
}
export function useMenteeCheckIns(menteeUserId: string | undefined) {
  return useQuery({
    queryKey: ['faculty', 'checkins', menteeUserId],
    enabled: !!menteeUserId,
    queryFn: () => apiGet<{ checkins: CheckIn[] }>(`/faculty/mentees/${menteeUserId}/checkins`),
  });
}
export function useLogCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      menteeUserId: string;
      format: 'person' | 'call' | 'whatsapp' | 'video' | 'email';
      text: string;
      riskFlags?: string[];
      flaggedToTpo?: boolean;
      suggestedNextStep?: string;
    }) => apiPost(`/faculty/mentees/${input.menteeUserId}/checkin`, {
      format: input.format,
      text: input.text,
      riskFlags: input.riskFlags ?? [],
      flaggedToTpo: input.flaggedToTpo ?? false,
      suggestedNextStep: input.suggestedNextStep,
    }),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['faculty', 'checkins', vars.menteeUserId] });
      qc.invalidateQueries({ queryKey: ['faculty', 'mentees'] });
    },
  });
}

export function usePrincipalOverview() {
  return useQuery({
    queryKey: ['principal', 'overview'],
    queryFn: () => apiGet<PrincipalOverviewResponse>('/principal/overview'),
  });
}

export function useCreateMou() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      partner: string;
      kind: 'Placement' | 'Internship' | 'Training' | 'Combined' | 'Custom';
      term: { startsAt: string; endsAt: string };
      scope?: { colleges?: string[]; programs?: string[]; specializations?: string[] };
    }) => apiPost('/group/mous', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'mous'] }),
  });
}
export function useRenewMou() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; term: { startsAt: string; endsAt: string } }) =>
      apiPost(`/group/mous/${input.id}/renew`, { term: input.term }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'mous'] }),
  });
}
export function useSignMou() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; role: 'partner' | 'tenant'; signedByName: string; designation?: string }) =>
      apiPost(`/group/mous/${input.id}/sign`, {
        role: input.role,
        signedByName: input.signedByName,
        designation: input.designation,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'mous'] }),
  });
}

export interface GroupBillingResponse {
  subscription: {
    _id: string;
    status: string;
    billingCycle: string;
    term: { startsAt: string; endsAt: string };
    seats: { allowed: number; used: number };
    aiCapOverride?: number;
    discount?: { percent: number; note?: string };
    po?: { number: string };
    billing: { gstin?: string; invoiceEmails: string[] };
    plan?: { code: string; name: string; price: { amount: number | null; unit: string } };
  } | null;
  invoices: Array<{
    _id: string;
    invoiceNumber: string;
    status: string;
    totalInr: number;
    balanceInr: number;
    issuedAt: string;
    dueAt: string;
    paidAt?: string;
    period?: { from: string; to: string };
    lineItems?: Array<{ description: string; subtotalInr: number }>;
  }>;
}
export interface ComplianceRow {
  _id: string;
  regulator: 'AICTE' | 'NAAC' | 'NBA' | 'NIRF' | 'UGC' | 'DTE' | 'Other';
  name: string;
  cycle?: string;
  description?: string;
  dueDate: string;
  submittedAt?: string;
  acceptedAt?: string;
  status: 'planned' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue';
  daysToDue: number;
  derivedStatus: string;
  notes?: string;
  evidenceUrl?: string;
}
export function useCompliance() {
  return useQuery({
    queryKey: ['group', 'compliance'],
    queryFn: () => apiGet<{ submissions: ComplianceRow[] }>('/group/compliance'),
  });
}
export function useCreateCompliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      regulator: 'AICTE' | 'NAAC' | 'NBA' | 'NIRF' | 'UGC' | 'DTE' | 'Other';
      name: string;
      cycle?: string;
      dueDate: string;
      description?: string;
    }) => apiPost('/group/compliance', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'compliance'] }),
  });
}
export function useUpdateCompliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      status: 'planned' | 'in_progress' | 'submitted' | 'accepted' | 'rejected';
      submittedAt?: string;
      evidenceUrl?: string;
      notes?: string;
    }) => apiPatch(`/group/compliance/${input.id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'compliance'] }),
  });
}

export interface AuditRow {
  _id: string;
  action: string;
  actor?: { userId?: string; role?: string; fullName?: string };
  actorUserId?: { fullName?: string; role?: string };
  actorRole?: string;
  ip?: string;
  at: string;
  subject?: { kind?: string; id?: string };
  meta?: Record<string, unknown>;
  reason?: string;
  /** Populated by the audit-list endpoint. Falls back to `{}` when the tenant was removed. */
  tenantId?: { _id?: string; name?: string };
}

/** Returns the shared { rows, page, limit, total, totalPages } shape; kept legacy `entries` alias
 *  as a thin adapter so pages that expect `entries` still work. */
export function usePlatformAudit(opts?: { action?: string; actor?: string; q?: string; limit?: number; page?: number }) {
  const params = new URLSearchParams();
  if (opts?.action) params.set('action', opts.action);
  if (opts?.actor) params.set('actor', opts.actor);
  if (opts?.q) params.set('q', opts.q);
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.page) params.set('page', String(opts.page));
  const qstr = params.toString();
  return useQuery({
    queryKey: ['platform', 'audit', qstr],
    queryFn: async () => {
      const r = await apiGet<{ rows: AuditRow[]; total: number; page: number; limit: number; totalPages: number }>(
        `/platform/audit${qstr ? `?${qstr}` : ''}`,
      );
      // Backward-compat: keep the `entries` alias the old audit UI used.
      return { ...r, entries: r.rows };
    },
  });
}

export function useGroupBilling() {
  return useQuery({
    queryKey: ['group', 'billing'],
    queryFn: () => apiGet<GroupBillingResponse>('/group/billing'),
  });
}

export function useGroupMous() {
  return useQuery({
    queryKey: ['group', 'mous'],
    queryFn: () => apiGet<{ mous: Array<{ _id: string; mouNumber: string; partner: { name: string }; term: { startsAt: string; endsAt: string }; status: string; daysToExpiry: number | null; expiryFlag: string | null }> }>('/group/mous'),
  });
}

export function useNirfExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ jobId: string; reportId: string; readyBy: string; status: string }>('/group/reports/nirf'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['group', 'reports'] }),
  });
}

export interface ReportRow {
  _id: string;
  jobId: string;
  kind: string;
  status: 'queued' | 'running' | 'ready' | 'sent' | 'failed';
  requestedAt: string;
  readyAt?: string;
  failedAt?: string;
  failureReason?: string;
  rowsIncluded?: number;
  warnings: string[];
  downloadUrl?: string;
  format: string;
}
export function useGroupReports(opts?: { autoPoll?: boolean }) {
  return useQuery({
    queryKey: ['group', 'reports'],
    queryFn: () => apiGet<{ reports: ReportRow[] }>('/group/reports'),
    refetchInterval: (query) => {
      if (opts?.autoPoll === false) return false;
      const rows = query.state.data?.reports ?? [];
      // Keep polling every 8s while any row is still working.
      const active = rows.some((r) => r.status === 'queued' || r.status === 'running');
      return active ? 8000 : false;
    },
  });
}

// ────────────────────────── F73 DPDP export + delete ──────────────────────────
export interface ExportRecord {
  _id: string;
  requestedAt: string;
  status: 'pending' | 'ready' | 'failed' | 'expired';
  fileSizeBytes?: number;
  expiresAt?: string;
  failureReason?: string;
  downloadUrl?: string;
}
export interface DeletionRecord {
  _id: string;
  requestedAt: string;
  effectiveAt: string;
  status: 'pending' | 'cancelled' | 'completed' | 'failed';
  reason?: string;
  retentionNotes: string[];
  cancelledAt?: string;
}
export function useExports() {
  return useQuery({
    queryKey: ['dpdp', 'exports'],
    queryFn: () => apiGet<{ exports: ExportRecord[] }>('/dpdp/exports'),
  });
}
export function useCreateExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ request: ExportRecord; downloadUrl: string }>('/dpdp/exports'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'exports'] }),
  });
}
export function useDeletionStatus() {
  return useQuery({
    queryKey: ['dpdp', 'deletion'],
    queryFn: () => apiGet<{ request: DeletionRecord | null }>('/dpdp/deletion'),
  });
}
export function useRequestDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason?: string }) => apiPost<{ request: DeletionRecord; alreadyPending?: boolean }>('/dpdp/deletion', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'deletion'] }),
  });
}
export function useCancelDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ request: DeletionRecord }>('/dpdp/deletion/cancel'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'deletion'] }),
  });
}

// ────────────────────────── F72 Prep plan ──────────────────────────
export interface PrepTaskRow {
  _id: string;
  label: string;
  kind: 'mock' | 'test' | 'reading' | 'project' | 'admin';
  estMinutes?: number;
  href?: string;
  done: boolean;
  doneAt?: string;
}
export interface PrepWeekRow {
  weekIndex: number;
  focus: string;
  rationale?: string;
  tasks: PrepTaskRow[];
}
export interface PrepPlanDoc {
  _id: string;
  targetRole: string;
  horizonWeeks: number;
  generatedAt: string;
  summary: string;
  contextSnapshot: {
    cgpa: number;
    skills: string[];
    profile360Score: number;
    upcomingInterviewCount: number;
    recentMockScore?: number;
  };
  weeks: PrepWeekRow[];
}
export function usePrepPlan() {
  return useQuery({
    queryKey: ['student', 'prep-plan'],
    queryFn: () => apiGet<{ plan: PrepPlanDoc | null }>('/students/me/prep-plan'),
  });
}
export function useRegeneratePrepPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { targetRole: string; horizonWeeks?: number }) => apiPost<{ plan: PrepPlanDoc }>('/students/me/prep-plan', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student', 'prep-plan'] }),
  });
}
export function useToggleTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { weekIndex: number; taskId: string; done: boolean }) => apiPost<{ plan: PrepPlanDoc }>('/students/me/prep-plan/task', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student', 'prep-plan'] }),
  });
}

// ────────────────────────── F71 Marksheet OCR ──────────────────────────
export interface OcrSubject {
  code: string;
  name: string;
  credits: number;
  grade: string;
  gradePoint?: number;
  marks?: number;
}
export interface OcrResult {
  semester: number;
  sgpa?: number;
  cgpa?: number;
  totalCredits?: number;
  subjects: OcrSubject[];
  confidence: 'hi' | 'lo';
  warnings: string[];
}
export function useOcrMarksheet() {
  return useMutation({
    mutationFn: (input: { imageUrl: string }) => apiPost<{ ocr: OcrResult }>('/students/me/marksheet/ocr', input),
  });
}
export function useConfirmMarksheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { semester: number; sgpa?: number; cgpa?: number; totalCredits?: number; subjects: OcrSubject[] }) =>
      apiPost('/students/me/marksheet/confirm', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student', 'profile-360'] }),
  });
}

// ────────────────────────── F70 Fraud queue ──────────────────────────
export interface FraudSignal {
  code: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  evidence?: string;
}
export interface FlaggedOpportunity {
  _id: string;
  title: string;
  slug: string;
  status: string;
  createdAt: string;
  partner?: { _id: string; name: string; verifiedAt?: string };
  fraudCheck: {
    riskScore: number;
    shouldFlag: boolean;
    signals: FraudSignal[];
    lastRunAt: string;
  };
}
export function useFlaggedOpportunities() {
  return useQuery({
    queryKey: ['platform', 'fraud', 'opportunities'],
    queryFn: () => apiGet<{ flagged: FlaggedOpportunity[] }>('/platform/fraud/opportunities'),
  });
}
export function useClearFraudFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      apiPost(`/platform/fraud/opportunities/${input.id}/clear`, { reason: input.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'fraud'] }),
  });
}
export function useRerunFraudCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ fraudCheck: { riskScore: number; shouldFlag: boolean; signals: string[]; lastRunAt: string } }>(
        `/platform/fraud/opportunities/${id}/rerun`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'fraud'] }),
  });
}

// ────────────────────────── F69 Career advisor ──────────────────────────
export interface CareerPath {
  pathName: string;
  fit: 'strong' | 'moderate' | 'stretch';
  milestones: Array<{ label: string; timeframe: string }>;
  gaps: string[];
  salaryBand: { p25: number; p50: number; p75: number };
  groundedIn: string[];
}
export interface AdvisorResult {
  paths: CareerPath[];
  narrative: string;
  suggestedNextSteps: string[];
  marketNote: string;
}
export function useAdvisor() {
  return useMutation({
    mutationFn: () => apiPost<{ advisor: AdvisorResult }>('/students/me/advisor'),
  });
}

// ────────────────────────── v1.6 §7.9 Portable Badges ──────────────────────────
export interface MyBadge {
  _id: string;
  skill: string;
  taxonomy: 'skill' | 'psychocognitive' | 'realwork' | 'language' | 'soft_skill' | 'domain';
  level: string;
  levelRank: number;
  score: number;
  verified: boolean;
  certified: boolean;
  issuerTier: 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4';
  issuer?: { _id: string; name?: string; slug?: string; logo?: string };
  issuedAt: string;
  expiresAt?: string;
  status: 'active' | 'provisional' | 'expired' | 'revoked' | 'superseded' | 'dormant';
}
export function useMyBadges(opts?: { includeInactive?: boolean }) {
  const includeInactive = opts?.includeInactive === true;
  const qs = includeInactive ? '?includeInactive=1' : '';
  return useQuery({
    queryKey: ['students', 'me', 'badges', includeInactive ? 'all' : 'active'],
    queryFn: () => apiGet<{ rows: MyBadge[] }>(`/students/me/badges${qs}`),
  });
}

/**
 * Single badge with populated issuer + workflowRun provenance. Used by the badge-detail
 * page reachable from Step 46/47 notifications and from the Profile badge strip.
 */
export interface MyBadgeDetail extends MyBadge {
  revokedAt?: string;
  revokedReason?: string;
  provenance?: {
    stageKey?: string;
    workflowRun?: {
      _id: string;
      currentStage?: string;
      workflow?: { _id: string; name?: string; version?: number; scope?: string; type?: string };
      history?: Array<{ fromStage: string; toStage: string; at: string; note?: string }>;
    };
  };
}
export function useMyBadge(id: string | null | undefined) {
  return useQuery({
    queryKey: ['students', 'me', 'badges', 'detail', id],
    queryFn: () => apiGet<{ badge: MyBadgeDetail }>(`/students/me/badges/${id}`),
    enabled: Boolean(id),
  });
}

// ────────────────────────── v1.6 §10 Partner earnings ──────────────────────────
export interface PartnerEarnings {
  partnerId: string;
  lifetime: { totalInr: number; succeededInr: number; pendingInr: number; txnCount: number };
  byMonth: Array<{ month: string; succeededInr: number; pendingInr: number }>;
}
export function usePartnerEarnings(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: ['partners', partnerId, 'earnings'],
    queryFn: () => apiGet<PartnerEarnings>(`/partners/${partnerId}/earnings`),
    enabled: Boolean(partnerId),
  });
}

// ────────────────────────── v1.6 §7.6 Workflow templates ──────────────────────────
export interface WorkflowStageLite {
  key: string;
  label: string;
  order: number;
  author: string;
  /** v1.6 §7.6 addendum — optional stage-type discriminator; 'assessment' triggers the picker. */
  stageType?: 'application' | 'shortlist' | 'assessment' | 'interview' | 'offer' | 'onboarding' | 'training' | 'custom';
  /** Only set when stageType === 'assessment'. Assessment._id. */
  assessmentId?: string;
  /** Per-stage config (passingScore, retryAllowed, etc.). Shape depends on stageType. */
  config?: Record<string, unknown>;
  reversibilityClass: string;
  allowedNext: string[];
  satisfiedByBadge?: {
    skill: string;
    minLevel: string;
    minLevelRank?: number;
    maxAgeDays?: number;
    requireVerified?: boolean;
    requireCertified?: boolean;
    requireIssuerTier?: 'tier_1' | 'tier_2' | 'any';
  };
  emitsBadge?: {
    skill: string;
    taxonomy: string;
    certified?: boolean;
    validForDays?: number;
    issuer?: 'kollez' | 'partner' | 'tenant';
  };
  monetization?: {
    model: string;
    chargeOn?: string;
    fee?: { amountInr: number; currency: 'INR' };
  };
}
export interface WorkflowTemplate {
  _id: string;
  name: string;
  version: number;
  scope: 'GLOBAL' | 'TENANT' | 'PARTNER';
  hostedBy?: { _id: string; name?: string; slug?: string; tenantType?: string } | string;
  type: string;
  description?: string;
  status: 'draft' | 'live' | 'retired';
  publishedAt?: string;
  retiredAt?: string;
  stages: WorkflowStageLite[];
}
export function useWorkflows(params?: { status?: string; type?: string; scope?: string }) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.type) qs.set('type', params.type);
  if (params?.scope) qs.set('scope', params.scope);
  return useQuery({
    queryKey: ['workflows', params?.status, params?.type, params?.scope],
    queryFn: () => apiGet<{ rows: WorkflowTemplate[] }>(`/workflows${qs.toString() ? `?${qs}` : ''}`),
  });
}
export function useWorkflow(id: string | null | undefined) {
  return useQuery({
    queryKey: ['workflows', id],
    queryFn: () => apiGet<{ workflow: WorkflowTemplate }>(`/workflows/${id}`),
    enabled: Boolean(id),
  });
}

export interface CreateWorkflowInput {
  name: string;
  scope: 'GLOBAL' | 'TENANT' | 'PARTNER';
  hostedBy?: string;
  type: string;
  description?: string;
  stages: WorkflowStageLite[];
}
export function useCreateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWorkflowInput) => apiPost<{ workflow: WorkflowTemplate }>('/workflows', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['workflows'] }); },
  });
}
export function usePatchWorkflow(id: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CreateWorkflowInput>) => apiPatch<{ workflow: WorkflowTemplate }>(`/workflows/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] });
      if (id) void qc.invalidateQueries({ queryKey: ['workflows', id] });
    },
  });
}
export function usePublishWorkflow(id: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ workflow: WorkflowTemplate }>(`/workflows/${id}/publish`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] });
      if (id) void qc.invalidateQueries({ queryKey: ['workflows', id] });
    },
  });
}
export function useRetireWorkflow(id: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ workflow: WorkflowTemplate }>(`/workflows/${id}/retire`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] });
      if (id) void qc.invalidateQueries({ queryKey: ['workflows', id] });
    },
  });
}

// ────────────────────────── v1.6 §7.13 Skill Taxonomy ──────────────────────────
export interface SkillTaxonomyRow {
  _id: string;
  slug: string;
  displayName: string;
  taxonomy: 'skill' | 'psychocognitive' | 'realwork' | 'language' | 'soft_skill' | 'domain';
  aliases: string[];
  levelLadder?: Array<{ level: string; rank: number }>;
  defaultExpiryDays?: number;
}
export function useSkillTaxonomy() {
  return useQuery({
    queryKey: ['skill-taxonomy'],
    queryFn: () => apiGet<{ rows: SkillTaxonomyRow[] }>('/skill-taxonomy'),
    // Canonical vocab changes rarely; cache aggressively.
    staleTime: 5 * 60 * 1000,
  });
}
export interface SkillTaxonomyDetail extends SkillTaxonomyRow {
  status?: 'live' | 'draft' | 'deprecated';
  demand: { required: number; preferred: number };
  badgesIssued: number;
}
export function useSkillTaxonomyDetail(slug: string | undefined) {
  return useQuery({
    enabled: Boolean(slug),
    queryKey: ['skill-taxonomy', slug],
    queryFn: () => apiGet<SkillTaxonomyDetail>(`/skill-taxonomy/${slug}`),
    staleTime: 60 * 1000,
  });
}

// GET /students/:id/skills-gap — Pass 11. Missing skills sorted by demand.
export interface StudentSkillsGapResponse {
  studentUserId: string;
  branch: string | null;
  opportunitiesConsidered: number;
  ownedSkills: string[];
  missingRequired: Array<{ skill: string; requiredIn: number }>;
  missingPreferred: Array<{ skill: string; preferredIn: number }>;
}
export function useStudentSkillsGap(userId: string | undefined, limit = 20) {
  return useQuery({
    enabled: Boolean(userId),
    queryKey: ['student-skills-gap', userId, limit],
    queryFn: () => apiGet<StudentSkillsGapResponse>(`/students/${userId}/skills-gap?limit=${limit}`),
  });
}

// ────────────────────────── v1.6 §7.6 WorkflowRuns ──────────────────────────
/**
 * Populated Badge shape on WorkflowRun history entries. The API populate paths
 * `history.satisfiedByBadge` and `history.emittedBadge` swap the ObjectId ref for this
 * enriched projection so the timeline can render provenance without a per-row fetch.
 */
export interface WorkflowRunHistoryBadge {
  _id: string;
  skill: string;
  level?: string;
  issuerTier?: 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4';
  issuer?: { _id: string; name?: string; slug?: string; tenantType?: string };
  issuedAt?: string;
  verified?: boolean;
  certified?: boolean;
}
export interface WorkflowRunHistoryEntry {
  fromStage: string;
  toStage: string;
  at: string;
  by?: { _id?: string; fullName?: string; role?: string } | null;
  auto?: boolean;
  /** Either the ObjectId (older rows) or the populated Badge projection (post-Step 36). */
  satisfiedByBadge?: string | WorkflowRunHistoryBadge;
  emittedBadge?: string | WorkflowRunHistoryBadge;
  note?: string;
  integrityFlags?: string[];
  monetizationTxn?: string;
}
export interface WorkflowRunDetail {
  _id: string;
  workflow?: { _id: string; name: string; version: number; scope: string; stages: WorkflowStageLite[] } | string;
  workflowVersion: number;
  application: string;
  currentStage: string;
  enteredCurrentStageAt: string;
  autoSatisfiedStages: string[];
  history: WorkflowRunHistoryEntry[];
  status: 'active' | 'completed' | 'abandoned' | 'terminated' | 'flagged';
  createdAt: string;
  updatedAt: string;
}
export function useApplicationRun(applicationId: string | null | undefined) {
  return useQuery({
    queryKey: ['workflow-runs', 'by-application', applicationId],
    queryFn: () => apiGet<{ run: WorkflowRunDetail | null }>(`/workflow-runs/by-application/${applicationId}`),
    enabled: Boolean(applicationId),
  });
}

/**
 * Row shape for the tenant WorkflowRuns index. Server populates workflow +
 * application for label rendering; per-row student pull happens on demand.
 */
export interface WorkflowRunRow {
  _id: string;
  workflow: { _id: string; name: string; status?: string; version?: number } | string;
  workflowVersion?: number;
  application: { _id: string; status?: string; student?: string } | string;
  student?: string;
  tenant?: string;
  partner?: string;
  currentStage: string;
  enteredCurrentStageAt: string;
  status: 'active' | 'completed' | 'abandoned' | 'terminated' | 'flagged';
  autoSatisfiedStages: string[];
  createdAt: string;
  updatedAt: string;
}
export interface WorkflowRunListParams {
  page?: number;
  limit?: number;
  workflow?: string;
  application?: string;
  student?: string;
  tenant?: string;
  status?: WorkflowRunRow['status'] | '';
  currentStage?: string;
  from?: string;
  to?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'currentStage';
  sortDir?: 'asc' | 'desc';
}
export function useWorkflowRunsList(params: WorkflowRunListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['workflow-runs', 'list', params],
    queryFn: () => apiGet<{
      runs: WorkflowRunRow[];
      summary: { byStatus: Record<string, number> };
      meta?: { page: number; limit: number; total: number };
    }>(`/workflow-runs?${qs.toString()}`),
  });
}

export interface WorkflowRunSummary {
  currentStage: string;
  status: 'active' | 'completed' | 'abandoned' | 'terminated' | 'flagged';
  workflowName: string;
  autoSatisfied: number;
}
/**
 * Batch summary for list surfaces. Sorts + joins ids into a stable cache key so multiple
 * mounts on the same list share one network call.
 */
export function useRunSummaries(applicationIds: string[] | null | undefined) {
  const stableIds = (applicationIds ?? []).slice().sort();
  const key = stableIds.join(',');
  return useQuery({
    queryKey: ['workflow-runs', 'summarize', key],
    queryFn: () => apiPost<{ runs: Record<string, WorkflowRunSummary> }>('/workflow-runs/summarize', { applicationIds: stableIds }),
    enabled: stableIds.length > 0,
  });
}

// ────────────────────────── F68 Assessment results + review ──────────────────────────
export interface ResultRow {
  _id: string;
  student: { _id: string; fullName?: string; email?: string; phone?: string };
  status: 'in_progress' | 'submitted' | 'under_review' | 'abandoned';
  totalScore: number;
  pendingReview: number;
  startedAt: string;
  submittedAt?: string;
  passed?: boolean;
  integrityFlags: number;
}
export interface ResultsResponse {
  assessment: { _id: string; name: string; totalMarks: number; passingMarks?: number; status: string };
  results: ResultRow[];
  summary: {
    totalAttempts: number;
    submitted: number;
    underReview: number;
    passed: number;
    failed: number;
    flagged: number;
  };
}
export function useExamResults(examId: string | undefined) {
  return useQuery({
    enabled: Boolean(examId),
    queryKey: ['assessments', 'results', examId],
    queryFn: () => apiGet<ResultsResponse>(`/assessments/exams/${examId}/results`),
  });
}

// Part 3 (assessments decision log) — aggregate stats for the Results page top strip.
// Complements the roster returned by `useExamResults`. Empty-shape on 0 attempts.
export interface AggregateBucket { bucket: string; count: number }
export interface AggregatePerQuestion {
  questionId: string;
  stem: string;
  timesShown: number;
  correctPct: number;
}
export interface AssessmentAggregateResponse {
  assessment: { _id: string; name: string; status: string; totalMarks: number; passingMarks?: number };
  attemptCount: number;
  scoreDistribution: AggregateBucket[];
  perQuestion: AggregatePerQuestion[];
}
export function useAssessmentAggregateResults(examId: string | undefined) {
  return useQuery({
    enabled: Boolean(examId),
    queryKey: ['assessments', 'results', 'aggregate', examId],
    queryFn: () =>
      apiGet<AssessmentAggregateResponse>(`/assessments/exams/${examId}/results/aggregate`),
  });
}
export function useReviewAnswer(attemptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { questionId: string; score: number; reason: string }) =>
      apiPost<{ attempt: { _id: string; status: string; totalScore: number; pendingReview: number } }>(`/assessments/attempts/${attemptId}/review`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessments', 'attempt', attemptId] });
      qc.invalidateQueries({ queryKey: ['assessments', 'results'] });
    },
  });
}

// ────────────────────────── F67 Assessments (exams + take) ──────────────────────────
export interface ExamSectionInput {
  title: string;
  source: 'fixed' | 'pool';
  questions?: string[];
  drawSpec?: {
    fromBanks: string[];
    total: number;
    difficultyMix?: { easy?: number; medium?: number; hard?: number };
    bySkill?: Record<string, number>;
  };
  timeLimitMin?: number;
}
export interface ExamRow {
  _id: string;
  // F-099: the server aliases `title` to `name` on Assessment. Reads return both keys — declare
  // both so callers can pick whichever fits and don't get an undefined key at runtime.
  title?: string;
  name?: string;
  description?: string;
  status: 'draft' | 'live' | 'published' | 'closed' | 'archived';
  scoring: { totalMarks: number; passingMarks?: number; negativeMarking: boolean };
  window: { opensAt: string; closesAt: string };
  createdAt: string;
  publishedAt?: string;
}
export function useExams() {
  return useQuery({
    queryKey: ['assessments', 'exams'],
    queryFn: () => apiGet<{ assessments: ExamRow[] }>('/assessments/exams'),
  });
}
export function useExam(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['assessments', 'exam', id],
    queryFn: () => apiGet<{ assessment: ExamRow & { sections: ExamSectionInput[] } }>(`/assessments/exams/${id}`),
  });
}
export interface ExamAssignmentRow {
  _id: string;
  assessment: string;
  createdAt: string;
  createdBy?: string;
  expiresAt?: string;
  revokedAt?: string;
  revokedBy?: string;
  revokedReason?: string;
  assignees: Array<{
    user: string;
    firstOpenedAt?: string;
    firstAttemptId?: string;
    fullName?: string;
    email?: string;
  }>;
  note?: string;
}
export interface ExamAssignmentsResponse {
  assignments: ExamAssignmentRow[];
  summary: { active: number; revoked: number; expired: number };
  meta?: { page: number; limit: number; total: number };
}
export function useExamAssignments(
  examId: string | undefined,
  opts: { status?: 'active' | 'revoked' | 'expired' | ''; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (opts.status) qs.set('status', opts.status);
  if (opts.limit) qs.set('limit', String(opts.limit));
  qs.set('sortBy', 'createdAt');
  qs.set('sortDir', 'desc');
  return useQuery({
    enabled: Boolean(examId),
    queryKey: ['assessments', 'exam', examId, 'assignments', opts],
    queryFn: () => apiGet<ExamAssignmentsResponse>(`/assessments/exams/${examId}/assignments?${qs.toString()}`),
  });
}
export function useRevokeAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { assignmentId: string; reason?: string }) =>
      apiPost(`/assessments/assignments/${v.assignmentId}/revoke`, { reason: v.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', 'exam'] }),
  });
}
export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      examId: string;
      userIds?: string[];
      filter?: { program?: string; batch?: number; department?: string };
      expiresAt: string;
    }) =>
      apiPost<{ assignment: { _id: string; assignees: unknown[]; expiresAt: string } }>(
        `/assessments/exams/${v.examId}/assign`,
        { userIds: v.userIds, filter: v.filter, expiresAt: v.expiresAt },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', 'exam'] }),
  });
}
export function useCreateExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      description?: string;
      sections: ExamSectionInput[];
      scoring?: { totalMarks: number; passingMarks?: number; negativeMarking?: boolean };
      gates?: { faceMatch?: boolean; photoIdCheck?: boolean; minTimePerQuestionSec?: number };
      delivery?: { mode?: 'proctored' | 'unproctored' | 'invigilated'; randomizeQuestions?: boolean; showResultsImmediately?: boolean; allowAttempts?: number };
      window: { opensAt: string; closesAt: string };
      drive?: string;
    }) => apiPost<{ assessment: ExamRow }>('/assessments/exams', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', 'exams'] }),
  });
}
export function usePublishExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ assessment: ExamRow }>(`/assessments/exams/${id}/publish`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments'] }),
  });
}
export function useStartAttempt() {
  return useMutation({
    mutationFn: (examId: string) => apiPost<{
      attempt: { _id: string; status: string; materializedQuestions?: Array<{ section: string; questionId: string }> };
      // F-100: server returns this flag when the caller has already submitted; client redirects.
      alreadySubmitted?: boolean;
    }>(`/assessments/exams/${examId}/start`),
  });
}
export interface AttemptDetail {
  _id: string;
  student: string;
  exam: string;
  status: string;
  startedAt?: string;
  submittedAt?: string;
  totalScore?: number;
  pendingReview?: number;
  materializedQuestions?: Array<{ section: string; questionId: string }>;
  answers?: Array<{
    question: string;
    section?: string;
    value?: unknown;
    score?: number;
    aiScore?: number;
    aiConfidence?: number;
    reviewerScore?: number;
    reviewerReason?: string;
    graded?: boolean;
    submittedAt?: string;
  }>;
  sectionScores?: Array<{
    section: string;
    obtained?: number;
    maxMarks?: number;
    passed?: boolean;
  }>;
  integrityEvents?: Array<{
    at?: string;
    kind?: string;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    note?: string;
  }>;
  gates?: {
    faceMatch?: { passed?: boolean; note?: string };
    photoIdCheck?: { passed?: boolean; note?: string };
    minTimePerQuestionSec?: number;
  };
}
export function useAttempt(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['assessments', 'attempt', id],
    queryFn: () => apiGet<{ attempt: AttemptDetail }>(`/assessments/attempts/${id}`),
  });
}
export interface AttemptQuestion {
  _id: string;
  stem: string;
  type: string;
  options?: Array<{ text: string }>;
  marks: number;
  timeGuideSec?: number;
  skill?: string;
  difficulty?: string;
  wrongPenalty?: number;
  explanation?: string;
}
export function useAttemptQuestion(attemptId: string | undefined, questionId: string | undefined) {
  return useQuery({
    enabled: Boolean(attemptId && questionId),
    queryKey: ['assessments', 'attempt', attemptId, 'question', questionId],
    queryFn: () => apiGet<{ question: AttemptQuestion }>(`/assessments/attempts/${attemptId}/question/${questionId}`),
    staleTime: 60 * 60 * 1000, // question text doesn't change during the attempt
  });
}
export function useSubmitAttemptAnswer(attemptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { questionId: string; value: unknown }) =>
      apiPost<{ score?: number; needsReview: boolean }>(`/assessments/attempts/${attemptId}/answer`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', 'attempt', attemptId] }),
  });
}
export function useFinalizeAttempt(attemptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ attempt: { _id: string; status: string; totalScore: number; pendingReview: number } }>(`/assessments/attempts/${attemptId}/submit`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments', 'attempt', attemptId] }),
  });
}

// ────────────────────────── F66 Mock interview ──────────────────────────
export interface MockQuestion {
  question: string;
  kind: 'behavioral' | 'technical' | 'system_design' | 'domain';
  difficulty: 'easy' | 'medium' | 'hard';
  idealAnswerHints: string[];
}
export interface MockAnswer {
  answer?: string;
  submittedAt?: string;
  score?: number;
  feedback?: string;
  strengths?: string[];
  gaps?: string[];
}
export interface MockSession {
  _id: string;
  targetRole: string;
  difficulty: 'easy' | 'medium' | 'hard';
  status: 'in_progress' | 'completed' | 'abandoned';
  startedAt: string;
  completedAt?: string;
  questions: MockQuestion[];
  answers: MockAnswer[];
}
export interface MockReport {
  _id: string;
  session: string;
  overallScore: number;
  strengths: string[];
  gaps: string[];
  suggestedNextSteps: string[];
  perQuestion: Array<{ question: string; score: number; feedback: string }>;
  deltaVsPrevious?: number;
  generatedAt: string;
}
export function useMockSessions() {
  return useQuery({
    queryKey: ['mock', 'sessions'],
    queryFn: () => apiGet<{ sessions: Array<Pick<MockSession, '_id' | 'targetRole' | 'difficulty' | 'status' | 'startedAt' | 'completedAt'> & { questionCount: number }> }>('/mock'),
  });
}
export function useMockSession(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['mock', id],
    queryFn: () => apiGet<{ session: MockSession; report: MockReport | null }>(`/mock/${id}`),
  });
}
export function useStartMock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { targetRole: string; difficulty: 'easy' | 'medium' | 'hard'; privacyOptIn?: boolean }) =>
      apiPost<{ session: MockSession }>('/mock', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mock', 'sessions'] }),
  });
}
export function useSubmitMockAnswer(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { questionIndex: number; answer: string }) =>
      apiPost<{ index: number; grade: { score: number; feedback: string; strengths: string[]; gaps: string[] } }>(`/mock/${sessionId}/answers`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mock', sessionId] }),
  });
}
export function useFinishMock(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ session: MockSession; report: MockReport }>(`/mock/${sessionId}/finish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mock', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['mock', sessionId] });
    },
  });
}

// ────────────────────────── F65 Resumes ──────────────────────────
export interface ResumeSummary {
  _id: string;
  name: string;
  variantOf?: string;
  targetOpportunity?: string;
  atsCheck?: { score: number };
  isPrimary?: boolean;
  updatedAt: string;
  createdAt: string;
  export?: { generatedAt?: string };
}
export interface ResumeFull extends ResumeSummary {
  sections: {
    summary?: string;
    skills?: string[];
    experience?: Array<{ title?: string; company?: string; location?: string; startMonth?: string; endMonth?: string; current?: boolean; bullets?: string[] }>;
    projects?: Array<{ name?: string; link?: string; stack?: string[]; bullets?: string[] }>;
    education?: Array<{ program?: string; institution?: string; startYear?: number; endYear?: number; score?: string }>;
    certifications?: Array<{ name?: string; issuer?: string; issuedOn?: string; credentialUrl?: string }>;
    awards?: Array<{ name?: string; issuer?: string; year?: number }>;
    links?: Array<{ label?: string; url?: string }>;
  };
  atsCheck?: {
    score: number;
    findings: Array<{ kind: 'warning' | 'suggestion' | 'error'; section?: string; message: string; fixHint?: string }>;
    checkedAt: string;
    checkedBy: 'nexie' | 'rule_based';
  };
}
export function useResumes() {
  return useQuery({
    queryKey: ['resumes'],
    queryFn: () => apiGet<{ resumes: ResumeSummary[] }>('/resumes'),
  });
}
export function useResume(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['resumes', id],
    queryFn: () => apiGet<{ resume: ResumeFull }>(`/resumes/${id}`),
  });
}
export function useCreateResume() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; variantOf?: string; targetOpportunity?: string; sections?: Partial<ResumeFull['sections']> }) =>
      apiPost<{ resume: ResumeFull }>('/resumes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resumes'] }),
  });
}
export function usePatchResume(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; sections?: Partial<ResumeFull['sections']>; isPrimary?: boolean }) =>
      apiPatch<{ resume: ResumeFull }>(`/resumes/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['resumes'] });
      qc.invalidateQueries({ queryKey: ['resumes', id] });
    },
  });
}
export function useDeleteResume() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/resumes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resumes'] }),
  });
}
export function useRunAts(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ atsCheck: ResumeFull['atsCheck'] }>(`/resumes/${id}/ats`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['resumes'] });
      qc.invalidateQueries({ queryKey: ['resumes', id] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost('/notifications/mark-all-read'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// -- Tenant picker (registration wizard, PR 4b) ------------------------

export interface TenantPickerHit {
  source: 'kollez' | 'aishe';
  tenantId?: string;
  aisheCollegeId?: string;
  name: string;
  slug?: string;
  aliasNames?: string[];
  breadcrumb?: string;
  tenantKind?: string;
  city?: string;
  district?: string;
  state?: string;
}

/**
 * Debounced tenant-picker search used by the register-student wizard.
 * Public endpoint — no auth required. Enabled only when q.length >= 2.
 * Identity-level: no activeTenantId in the key (pre-auth surface).
 */
export function useTenantSearch(q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: ['public', 'tenants', 'search', trimmed],
    enabled: trimmed.length >= 2,
    queryFn: () =>
      apiGet<{ q: string; count: number; hits: TenantPickerHit[] }>(
        `/public/tenants/search?q=${encodeURIComponent(trimmed)}`,
      ),
    staleTime: 30_000,
  });
}

/**
 * Tenant admin onboards a new partner into their own tenant scope.
 * Used by /group/partners and /tpo/partners. Both endpoints share the same handler on the server;
 * the caller's tenant scope decides which tenant the partner lands in.
 */
export interface CreatePartnerBody {
  name: string;
  type: string;               // PartnerType — enum-checked by the server schema
  industry?: string;
  companySize?: string;
  website?: string;
  description?: string;
  /** Group / university admins must pick a leaf college. Leaf-level callers omit this. */
  collegeId?: string;
  /**
   * Required — every Partner is created together with its admin. The server auto-provisions the
   * partner_admin User + a PartnerInvite. The admin activates via /accept-invite?token=…
   */
  admin: {
    fullName: string;
    email: string;
    phone?: string;
  };
}

export interface CreatePartnerResponse {
  partner: { _id: string; name: string; slug: string; type: string; tenant?: string };
  admin: { userId: string; email: string; fullName: string; inviteLink: string };
}

export interface PartnerContact {
  _id: string;
  name: string;
  role?: string;
  email: string;
  phone?: string;
  addedAt: string;
  removedAt?: string;
}

export interface PartnerUserRow {
  _id: string;
  fullName: string;
  email: string;
  phone?: string;
  role: string;
  lastLoginAt?: string;
  addedAt?: string;
}
export interface PartnerPendingInvite {
  _id: string;
  email: string;
  role: string;
  fullName?: string;
  sentAt: string;
  expiresAt: string;
}

export interface GroupCollegeOption { _id: string; name: string; slug?: string; }

export function useGroupCollegesList() {
  return useQuery({
    queryKey: ['group', 'colleges', 'pickerList'],
    queryFn: () => apiGet<{ rows: GroupCollegeOption[]; total: number }>('/group/colleges?limit=200&sort=name&order=asc'),
    staleTime: 60_000,
  });
}

export function useCreateGroupPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartnerBody) =>
      apiPost<CreatePartnerResponse>('/group/partners', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group', 'partners'] });
      qc.invalidateQueries({ queryKey: ['tpo', 'companies'] });
    },
  });
}

/**
 * Same shape, different URL — TPO-side surface. The server's /tpo/partners route delegates to
 * the same createGroupPartnerHandler, so tenant scope resolves to the caller's college tenant.
 */
export function useCreateTpoPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartnerBody) =>
      apiPost<CreatePartnerResponse>('/tpo/partners', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tpo', 'companies'] });
      qc.invalidateQueries({ queryKey: ['group', 'partners'] });
    },
  });
}

/**
 * Detail + edit + deactivate + related-content hooks for the TPO CompanyDetail (and reusable
 * from the group PartnerDetail). Both /tpo/partners/:id and /group/partners/:id are wired to
 * the same handler on the server, so the choice of URL just tracks which shell the user is in.
 */
export interface PartnerDetail {
  _id: string;
  name: string;
  slug: string;
  type: string;
  industry?: string;
  companySize?: string;
  website?: string;
  description?: string;
  status: string;
  tenant: string;
  isVerified?: boolean;
  contacts?: PartnerContact[];
  createdAt?: string;
}
export interface PartnerMou {
  _id: string;
  status: string;
  term: { startsAt: string; endsAt: string };
  kind?: string;
  signatures?: Array<{ role: string; signedAt?: string; signedByName?: string }>;
  mouNumber?: string;
}
export interface PartnerOpportunityRow {
  _id: string;
  title: string;
  role?: string;
  status: string;
  openings?: number;
  compensation?: { ctc?: { min?: number; max?: number } };
  createdAt?: string;
  closedAt?: string;
}

function partnersRootFor(scope: 'group' | 'tpo'): string {
  return scope === 'group' ? '/group/partners' : '/tpo/partners';
}

export function useGroupPartnerById(id: string | undefined, scope: 'group' | 'tpo' = 'tpo') {
  return useQuery({
    queryKey: [scope, 'partner', id],
    queryFn: () => apiGet<{ partner: PartnerDetail }>(`${partnersRootFor(scope)}/${id}`),
    enabled: !!id,
  });
}

export function useUpdateGroupPartner(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CreatePartnerBody & { status: string }> }) =>
      apiPatch<{ partner: PartnerDetail }>(`${partnersRootFor(scope)}/${id}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.id] });
      qc.invalidateQueries({ queryKey: ['group', 'partners'] });
      qc.invalidateQueries({ queryKey: ['tpo', 'companies'] });
    },
  });
}

export function useDeactivateGroupPartner(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiPost<{ partner: PartnerDetail }>(`${partnersRootFor(scope)}/${id}/deactivate`, { reason }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.id] });
      qc.invalidateQueries({ queryKey: ['group', 'partners'] });
      qc.invalidateQueries({ queryKey: ['tpo', 'companies'] });
    },
  });
}

export function useReactivateGroupPartner(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiPost<{ partner: PartnerDetail }>(`${partnersRootFor(scope)}/${id}/reactivate`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.id] });
    },
  });
}

export function useGroupPartnerMous(id: string | undefined, scope: 'group' | 'tpo' = 'tpo') {
  return useQuery({
    queryKey: [scope, 'partner', id, 'mous'],
    queryFn: () => apiGet<{ mous: PartnerMou[] }>(`${partnersRootFor(scope)}/${id}/mous`),
    enabled: !!id,
  });
}

export function useGroupPartnerOpportunities(id: string | undefined, scope: 'group' | 'tpo' = 'tpo') {
  return useQuery({
    queryKey: [scope, 'partner', id, 'opps'],
    queryFn: () => apiGet<{ opportunities: PartnerOpportunityRow[] }>(`${partnersRootFor(scope)}/${id}/opportunities`),
    enabled: !!id,
  });
}

/**
 * Partner Contacts sub-doc — multi-POC list per partner. `email` uniqueness enforced server-side.
 * Soft-delete keeps rows in DB for audit.
 */
export interface AddPartnerContactBody { name: string; role?: string; email: string; phone?: string; }
export function useAddPartnerContact(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partnerId, body }: { partnerId: string; body: AddPartnerContactBody }) =>
      apiPost<{ partner: { _id: string; name: string }; contact: PartnerContact }>(
        `${partnersRootFor(scope)}/${partnerId}/contacts`, body,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.partnerId] });
    },
  });
}
export function useRemovePartnerContact(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partnerId, contactId }: { partnerId: string; contactId: string }) =>
      apiDelete<{ removed: boolean }>(`${partnersRootFor(scope)}/${partnerId}/contacts/${contactId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.partnerId] });
    },
  });
}

/**
 * MoU renew — creates a new MoU with `renewalOf` pointer, transitions the old one to `Renewed`.
 */
export interface RenewMoUBody {
  term: { startsAt: string; endsAt: string };
  changedClauses?: Array<{ key: string; oldValue?: unknown; newValue?: unknown; note?: string }>;
}
export function useRenewMoU() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mouId, body }: { mouId: string; body: RenewMoUBody }) =>
      apiPost<{ mou: PartnerMou; previous: { _id: string; mouNumber?: string } }>(
        `/group/mous/${mouId}/renew`, body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tpo', 'partner'] });
      qc.invalidateQueries({ queryKey: ['group', 'partner'] });
      qc.invalidateQueries({ queryKey: ['group', 'mous'] });
    },
  });
}

/**
 * Partner Users & Roles — every partner-side user + pending invites.
 */
export function useGroupPartnerUsers(partnerId: string | undefined, scope: 'group' | 'tpo' = 'tpo') {
  return useQuery({
    queryKey: [scope, 'partner', partnerId, 'users'],
    queryFn: () => apiGet<{
      partner: { _id: string; name: string };
      members: PartnerUserRow[];
      pendingInvites: PartnerPendingInvite[];
    }>(`${partnersRootFor(scope)}/${partnerId}/team`),
    enabled: !!partnerId,
  });
}

export interface InvitePartnerUserBody { email: string; role: string; fullName?: string; }
export function useInvitePartnerUser(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partnerId, body }: { partnerId: string; body: InvitePartnerUserBody }) =>
      apiPost<{
        invite: PartnerPendingInvite;
        inviteLink: string;
      }>(`${partnersRootFor(scope)}/${partnerId}/invite`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.partnerId, 'users'] });
    },
  });
}

export function useDeactivatePartnerUser(scope: 'group' | 'tpo' = 'tpo') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partnerId, userId }: { partnerId: string; userId: string }) =>
      apiPost<{ deactivated: boolean }>(
        `${partnersRootFor(scope)}/${partnerId}/team/${userId}/deactivate`, {},
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [scope, 'partner', vars.partnerId, 'users'] });
    },
  });
}

/**
 * Partner-invite inspect + accept — public endpoints used by /accept-invite page.
 */
export interface InspectPartnerInviteResponse {
  type: 'partner_invite';
  email: string;
  role: string;
  fullName?: string;
  partner: { _id: string; name: string; type: string; industry?: string };
  expiresAt: string;
  inviterRole: string;
}
export function useInspectPartnerInvite(token: string | undefined) {
  return useQuery({
    queryKey: ['partner-invite', token],
    queryFn: () => apiGet<InspectPartnerInviteResponse>(`/auth/partner-invite/${token}`),
    enabled: !!token,
    retry: false,
  });
}
export interface AcceptPartnerInviteBody { token: string; password: string; fullName?: string; phone?: string; }
export function useAcceptPartnerInvite() {
  return useMutation({
    mutationFn: (body: AcceptPartnerInviteBody) =>
      apiPost<{
        user: { id: string; fullName: string; email: string; role: string; partner: string; tenant: string };
        partner: { _id: string; name: string };
        memberships: unknown[];
        activeTenantId: null;
        tokens: { accessToken: string; refreshToken: string; expiresIn: number };
      }>('/auth/accept-partner-invite', body),
  });
}

// ────────────────────────── DPDP §13 grievances ──────────────────────────
// Data-subject rights channel. Every fiduciary must offer a written
// complaint route with a 90-day SLA. See apps/api/src/modules/dpdp/
// grievance.controller.ts for the server contract.
export type GrievanceCategory =
  | 'unauthorized_processing'
  | 'consent_violation'
  | 'data_leak'
  | 'ai_output_error'
  | 'discrimination'
  | 'other';

export type GrievanceStatus =
  | 'open' | 'acknowledged' | 'in_review' | 'resolved' | 'escalated' | 'closed';

export type GrievancePriority = 'low' | 'normal' | 'high' | 'urgent';

export interface GrievanceRow {
  _id: string;
  category: GrievanceCategory;
  subject: string;
  description: string;
  status: GrievanceStatus;
  priority: GrievancePriority;
  evidenceUrls?: string[];
  user?: { _id: string; fullName: string; email: string } | string;
  assignedTo?: { _id: string; fullName: string; email: string } | string | null;
  resolutionNote?: string;
  slaDeadline?: string;
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string;
}

export interface GrievanceListParams {
  page?: number;
  limit?: number;
  q?: string;
  status?: GrievanceStatus | '';
  priority?: GrievancePriority | '';
  category?: GrievanceCategory | '';
  mine?: boolean;
  slaBreached?: boolean;
  sortBy?: 'createdAt' | 'slaDeadline' | 'priority' | 'status';
  sortDir?: 'asc' | 'desc';
}

export function useGrievances(params: GrievanceListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['dpdp', 'grievances', params],
    queryFn: () => apiGet<{
      rows: GrievanceRow[];
      summary: Partial<Record<GrievanceStatus, number>>;
      meta?: { page: number; limit: number; total: number };
    }>(`/dpdp/grievances?${qs.toString()}`),
  });
}

export interface CreateGrievanceBody {
  category: GrievanceCategory;
  subject: string;
  description: string;
  evidenceUrls?: string[];
  priority?: GrievancePriority;
}

export function useCreateGrievance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGrievanceBody) =>
      apiPost<{ grievance: GrievanceRow }>('/dpdp/grievances', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dpdp', 'grievances'] });
    },
  });
}

export interface UpdateGrievanceBody {
  status: Exclude<GrievanceStatus, 'open'>;
  note?: string;
  escalatedTo?: 'platform_dpo' | 'tenant_dpo' | 'external_regulator';
}
export function useUpdateGrievance(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateGrievanceBody) =>
      apiPost<{ grievance: GrievanceRow }>(`/dpdp/grievances/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'grievances'] }),
  });
}

// ────────────────────────── DPDP §14 rectification ──────────────────────────
export type RectificationSubjectKind =
  | 'StudentProfile' | 'Application' | 'Offer' | 'Badge' | 'Membership'
  | 'Interview' | 'FacultyProfile' | 'Other';
export type RectificationStatus =
  | 'pending' | 'in_review' | 'approved' | 'rejected' | 'more_info_needed' | 'withdrawn';

export interface RectificationRow {
  _id: string;
  subject: { kind: RectificationSubjectKind; id: string };
  fieldPath: string;
  currentValue?: unknown;
  proposedValue?: unknown;
  justification: string;
  evidenceUrls?: string[];
  status: RectificationStatus;
  reviewedBy?: { _id: string; fullName: string } | string | null;
  reviewedAt?: string;
  reviewNote?: string;
  appliedAt?: string;
  slaDeadline?: string;
  user?: { _id: string; fullName: string; email: string } | string;
  createdAt: string;
  updatedAt?: string;
}

export interface RectificationListParams {
  page?: number;
  limit?: number;
  q?: string;
  status?: RectificationStatus | '';
  subjectKind?: RectificationSubjectKind | '';
  mine?: boolean;
  slaBreached?: boolean;
  sortBy?: 'createdAt' | 'slaDeadline' | 'status';
  sortDir?: 'asc' | 'desc';
}

export function useRectifications(params: RectificationListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['dpdp', 'rectifications', params],
    queryFn: () => apiGet<{
      rows: RectificationRow[];
      summary?: Partial<Record<RectificationStatus, number>>;
      meta?: { page: number; limit: number; total: number };
    }>(`/dpdp/rectification?${qs.toString()}`),
  });
}

export interface CreateRectificationBody {
  subject: { kind: RectificationSubjectKind; id: string };
  fieldPath: string;
  currentValue?: unknown;
  proposedValue: unknown;
  justification: string;
  evidenceUrls?: string[];
}
export function useCreateRectification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRectificationBody) =>
      apiPost<{ rectification: RectificationRow }>('/dpdp/rectification', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'rectifications'] }),
  });
}

export function useWithdrawRectification(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost(`/dpdp/rectification/${id}/withdraw`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'rectifications'] }),
  });
}

export interface RectificationDecideBody {
  decision: 'approve' | 'reject' | 'more_info';
  reviewNote?: string;
}
export function useDecideRectification(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RectificationDecideBody) =>
      apiPost<{ rectification: RectificationRow }>(`/dpdp/rectification/${id}/decide`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'rectifications'] }),
  });
}

// ────────────────────────── DPDP §6 consents ──────────────────────────
export interface ConsentRow {
  _id: string;
  user: string;
  purpose: string;                     // 'terms', 'placement_visibility', 'analytics', etc.
  granted: boolean;
  version?: string;
  grantedAt?: string;
  revokedAt?: string;
  meta?: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
}

export function useMyConsents() {
  return useQuery({
    queryKey: ['dpdp', 'consents'],
    queryFn: () => apiGet<{ consents: ConsentRow[] }>('/dpdp/consents'),
  });
}

export interface UpsertConsentBody {
  purpose: string;
  granted: boolean;
  version?: string;
}
export function useUpsertConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertConsentBody) =>
      apiPost<{ consent: ConsentRow }>('/dpdp/consents', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dpdp', 'consents'] }),
  });
}

// ────────────────────────── Assessment assignments ──────────────────────────
export interface StudentAssignmentRow {
  _id: string;
  assessment: {
    _id: string;
    name?: string;
    description?: string;
    status?: string;
    window?: { opensAt?: string; closesAt?: string };
    scoring?: { totalMarks?: number; passingMarks?: number };
  } | null;
  /**
   * Drive attach context. Set by the Shape-C attach flow when an assessment
   * is wired into a drive stage — the student sees "for Drive X · stage Y"
   * on their inbox row so it's obvious this test gates a specific application.
   */
  drive?: { _id: string; driveName?: string } | string;
  stageKey?: string;
  expiresAt?: string;
  assignedBy?: string | { _id: string; fullName?: string };
  assignee?: {
    user: string;
    startedAt?: string;
    submittedAt?: string;
    attemptId?: string;
  };
  createdAt: string;
}

/**
 * Drive assessments read model — one row per drive stage that could carry
 * an assessment (currently `roles[].stageWindows[]` on the Drive doc).
 * `assessment` is null when nothing is attached to that stage yet.
 */
export interface DriveStageAssessmentRow {
  stageKey: string;
  stageName?: string;
  roleId?: string;
  assessment: {
    _id: string;
    name?: string;
    duration?: { totalMinutes?: number };
    scoring?: { totalMarks?: number; passingMarks?: number };
  } | null;
  assignments: {
    total: number;
    assigned: number;
    started: number;
    submitted: number;
    expired: number;
  };
}

export function useDriveAssessments(driveId: string | undefined) {
  return useQuery({
    queryKey: ['drives', driveId, 'assessments'],
    enabled: Boolean(driveId),
    queryFn: () => apiGet<{ stages: DriveStageAssessmentRow[] }>(`/drives/${driveId}/assessments`),
  });
}

export function useAttachAssessmentToStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driveId, stageKey, assessmentId }: {
      driveId: string; stageKey: string; assessmentId: string;
    }) =>
      apiPost<{ drive: unknown; attached: number }>(
        `/drives/${driveId}/stages/${encodeURIComponent(stageKey)}/assessment`,
        { assessmentId },
      ),
    onSuccess: (_out, { driveId }) => {
      qc.invalidateQueries({ queryKey: ['drives', driveId, 'assessments'] });
      qc.invalidateQueries({ queryKey: ['drives', driveId] });
      qc.invalidateQueries({ queryKey: ['applications', driveId] });
    },
  });
}

export function useDetachAssessmentFromStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driveId, stageKey }: { driveId: string; stageKey: string }) =>
      apiPost<{ drive: unknown; detached: number }>(
        `/drives/${driveId}/stages/${encodeURIComponent(stageKey)}/assessment/detach`,
        {},
      ),
    onSuccess: (_out, { driveId }) => {
      qc.invalidateQueries({ queryKey: ['drives', driveId, 'assessments'] });
      qc.invalidateQueries({ queryKey: ['drives', driveId] });
      qc.invalidateQueries({ queryKey: ['applications', driveId] });
    },
  });
}
// ────────────────────────── Public catalog transactions ──────────────────────────
export type CatalogTxnStatus = 'pending' | 'succeeded' | 'failed' | 'refunded';

export interface CatalogTxnRow {
  _id: string;
  stageKey?: string;
  chargeOn?: string;
  monetizationModel?: string;
  amountInr: number;
  paymentMethod?: string;
  status: CatalogTxnStatus;
  initiatedAt?: string;
  settledAt?: string;
  workflow?: string;
  opportunity?: { _id: string; title?: string } | string | null;
  application?: string;
  partner?: { _id: string; name?: string; slug?: string } | string | null;
}

export interface CatalogTxnListParams {
  page?: number;
  limit?: number;
  status?: CatalogTxnStatus | '';
  from?: string;
  to?: string;
  sortBy?: 'initiatedAt' | 'settledAt' | 'amountInr';
  sortDir?: 'asc' | 'desc';
}

export function useMyCatalogTxns(params: CatalogTxnListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['student', 'me', 'transactions', params],
    queryFn: () => apiGet<{
      rows: CatalogTxnRow[];
      summary?: { byStatus: Record<string, { count: number; amountInr: number }>; totalPaidInr: number };
      meta?: { page: number; limit: number; total: number };
    }>(`/students/me/transactions?${qs.toString()}`),
  });
}

// ────────────────────────── Platform-wide revenue ledger ──────────────────────────
export interface PlatformTxnRow extends CatalogTxnRow {
  student?: string;
  studentPrimaryTenant?: { _id: string; name?: string } | string | null;
  split?: { kollezInr?: number; partnerInr?: number };
}

export interface PlatformTxnListParams {
  page?: number;
  limit?: number;
  status?: CatalogTxnStatus | '';
  partnerId?: string;
  tenantId?: string;
  from?: string;
  to?: string;
}

export interface PlatformTxnRollupRow {
  amountInr: number;
  kollezInr: number;
  partnerInr: number;
  count: number;
}

export function usePlatformTxns(params: PlatformTxnListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['platform', 'transactions', params],
    queryFn: () => apiGet<{
      rows: PlatformTxnRow[];
      rollup: Record<string, PlatformTxnRollupRow>;
      meta?: { page: number; limit: number; total: number };
    }>(`/platform/transactions?${qs.toString()}`),
  });
}

// ────────────────────────── Affiliations (Axis-4) ──────────────────────────
export type AffiliationType = 'Affiliated' | 'Constituent' | 'Autonomous' | 'Deemed';
export type AffiliationStatus = 'active' | 'expired' | 'terminated' | 'superseded';

export interface AffiliationRow {
  _id: string;
  type: AffiliationType;
  status: AffiliationStatus;
  startAt?: string;
  endAt?: string;
  college?: { _id: string; name: string; aisheCode?: string } | string;
  university?: { _id: string; name: string; aisheCode?: string } | string;
  supersededBy?: { _id: string; type?: AffiliationType; startAt?: string } | string;
  documentUrl?: string;
  note?: string;
}

export interface AffiliationListParams {
  page?: number;
  limit?: number;
  q?: string;
  collegeId?: string;
  universityId?: string;
  type?: AffiliationType | '';
  status?: AffiliationStatus | '';
  asOf?: string;
  sortBy?: 'startAt' | 'endAt' | 'type' | 'status' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}

export function useAffiliations(params: AffiliationListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['group', 'affiliations', params],
    queryFn: () => apiGet<{
      rows: AffiliationRow[];
      summary: { active: number; expired: number; terminated: number; superseded: number };
      meta?: { page: number; limit: number; total: number };
    }>(`/tenant/orgs/affiliations?${qs.toString()}`),
  });
}

// ────────────────────────── Tenant reactivate + AI-cap edit ──────────────────────────
export function useReactivateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { tenantId: string; reason: string }) =>
      apiPost<{ tenant: { _id: string; status: string } }>(
        `/platform/tenants/${v.tenantId}/reactivate`,
        { reason: v.reason },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      void qc.invalidateQueries({ queryKey: ['platform', 'tenant'] });
    },
  });
}
export function useSetTenantAiCap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { tenantId: string; monthlyBudgetInr: number; reason: string }) =>
      apiPatch<{ tenant: { _id: string; aiCap?: { monthlyBudgetInr: number } } }>(
        `/platform/tenants/${v.tenantId}/ai-cap`,
        { monthlyBudgetInr: v.monthlyBudgetInr, reason: v.reason },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      void qc.invalidateQueries({ queryKey: ['platform', 'tenant'] });
    },
  });
}

// ────────────────────────── Platform user actions ──────────────────────────
export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ userId: string; cleared: boolean }>(`/platform/users/${userId}/unlock`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['platform', 'users'] }); },
  });
}
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ userId: string; invited: boolean; email?: string }>(`/platform/users/${userId}/resend-invite`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['platform', 'users'] }); },
  });
}
export function useSignoutUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ userId: string; revokedCount: number }>(`/platform/users/${userId}/signout-all`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['platform', 'users'] }); },
  });
}

// ────────────────────────── Invoice mark-paid ──────────────────────────
export interface MarkPaidInput {
  invoiceId: string;
  utr: string;
  paidAt?: string;
  amountInr?: number;
  note?: string;
}
export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: MarkPaidInput) =>
      apiPost<{ invoice: { _id: string; status: string } }>(
        `/platform/invoices/${v.invoiceId}/mark-paid`,
        { utr: v.utr, paidAt: v.paidAt, amountInr: v.amountInr, note: v.note },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'invoices'] });
    },
  });
}

// ────────────────────────── Nexie auto-schedule ──────────────────────────
export interface AutoScheduleSlot {
  studentId: string;
  studentName?: string;
  panel: string;
  startAt: string;
  endAt: string;
  reason?: string;
}
export interface AutoScheduleProposal {
  slots: AutoScheduleSlot[];
  unassigned: Array<{ studentId: string; studentName?: string; reason: string }>;
  totalCapacity: number;
  narrative: string;
}
export interface AutoScheduleInput {
  driveId: string;
  panels: string[];
  windowStart: string;
  windowEnd: string;
  slotMinutes?: number;
}
export function useAutoSchedule() {
  return useMutation({
    mutationFn: (v: AutoScheduleInput) =>
      apiPost<{ proposal: AutoScheduleProposal }>('/nexie/auto-schedule', v),
  });
}

// ────────────────────────── MoU renewal chain ──────────────────────────
export interface MouChainNode {
  _id: string;
  status?: string;
  kind?: string;
  term?: { startsAt?: string; endsAt?: string; start?: string; end?: string };
  mouNumber?: string;
  changedClauses?: string[];
}

export interface MouChainGap {
  fromMouId: string;
  toMouId: string;
  daysGap: number | null;
}

export function useMouRenewals(id: string | undefined) {
  return useQuery({
    queryKey: ['group', 'mou', id, 'renewals'],
    queryFn: () => apiGet<{
      seedId: string;
      chainLength: number;
      chain: MouChainNode[];
      gaps: MouChainGap[];
      seedIndex: number;
    }>(`/group/mous/${id}/renewals`),
    enabled: Boolean(id),
  });
}

// ────────────────────────── Analytics agreements (DPDP §7.2) ──────────────────────────
export type AnalyticsAgreementStatus = 'Draft' | 'Signed' | 'Active' | 'Expiring' | 'Expired' | 'Terminated' | 'Renewed';

export interface AnalyticsAgreementRow {
  _id: string;
  agreementNumber?: string;
  title?: string;
  status: AnalyticsAgreementStatus;
  partner?: { _id: string; name?: string; slug?: string } | string;
  tenant?: { _id: string; slug?: string; displayName?: string } | string;
  term?: { startsAt?: string; endsAt?: string };
  scope?: string[];
  purposeStatement?: string;
  renewalOf?: string;
  renewedBy?: string;
}

export interface AnalyticsAgreementListParams {
  page?: number;
  limit?: number;
  q?: string;
  status?: AnalyticsAgreementStatus | '';
  partnerId?: string;
  tenantId?: string;
  expiringInDays?: number;
  sortBy?: 'term.startsAt' | 'term.endsAt' | 'createdAt' | 'status';
  sortDir?: 'asc' | 'desc';
}

export function useAnalyticsAgreements(params: AnalyticsAgreementListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['platform', 'analytics-agreements', params],
    queryFn: () => apiGet<{
      rows: AnalyticsAgreementRow[];
      summary: Record<string, number>;
      meta?: { page: number; limit: number; total: number };
    }>(`/platform/analytics-agreements?${qs.toString()}`),
  });
}

export function useAnalyticsAgreement(id: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'analytics-agreement', id],
    queryFn: () => apiGet<Record<string, unknown>>(`/platform/analytics-agreements/${id}`),
    enabled: !!id,
  });
}

// ────────────────────────── Rooms + Holidays (calendar) ──────────────────────────
export type HolidayKind = 'academic' | 'placement_freeze' | 'exam_week' | 'festival' | 'other';

export interface HolidayRow {
  _id: string;
  tenant: string;
  college: string;
  name: string;
  startDate: string;
  endDate: string;
  kind: HolidayKind;
  note?: string;
  isRecurring?: boolean;
}

export interface RoomBooking {
  from: string;
  to: string;
  purpose: 'drive' | 'interview' | 'exam' | 'class' | 'other';
  driveId?: string;
  interviewId?: string;
  reservedBy?: string;
  reservedAt?: string;
  note?: string;
}
export interface RoomRow {
  _id: string;
  tenant: string;
  college: string;
  name: string;
  code?: string;
  capacity?: number;
  seats?: number;
  building?: string;
  floor?: string;
  features?: string[];
  active?: boolean;
  bookings?: RoomBooking[];
}

export function useHolidays(params: {
  page?: number; limit?: number; q?: string;
  kind?: HolidayKind | ''; from?: string; to?: string;
} = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['calendar', 'holidays', params],
    queryFn: () => apiGet<HolidayRow[]>(`/calendar/holidays?${qs.toString()}`),
  });
}

export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      college: string; name: string; startDate: string; endDate: string;
      kind?: HolidayKind; note?: string; isRecurring?: boolean;
    }) => apiPost<HolidayRow>('/calendar/holidays', v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['calendar', 'holidays'] }); },
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/calendar/holidays/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['calendar', 'holidays'] }); },
  });
}

export function useRooms(params: {
  page?: number; limit?: number; q?: string;
  active?: 'true' | 'false' | '';
} = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['calendar', 'rooms', params],
    queryFn: () => apiGet<RoomRow[]>(`/calendar/rooms?${qs.toString()}`),
  });
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      college: string; name: string; code?: string; capacity?: number;
      building?: string; floor?: string; features?: string[];
    }) => apiPost<RoomRow>('/calendar/rooms', v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['calendar', 'rooms'] }); },
  });
}

export function useDeleteRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/calendar/rooms/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['calendar', 'rooms'] }); },
  });
}

// ────────────────────────── Partner transactions (Pool-2 ledger) ──────────────────────────
export interface PartnerTransactionRow {
  _id: string;
  stageKey?: string;
  chargeOn?: string;
  monetizationModel?: string;
  amountInr: number;
  paymentMethod?: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  initiatedAt: string;
  settledAt?: string;
  split?: {
    platformInr?: number;
    tenantInr?: number;
    partnerInr?: number;
    creatorInr?: number;
  };
  workflow?: string;
  opportunity?: string;
  application?: string;
  student?: string;
  studentPrimaryTenant?: string;
}
export function usePartnerTransactions(
  partnerId: string | null,
  opts: { page?: number; limit?: number; status?: string; from?: string; to?: string } = {},
) {
  const qs = new URLSearchParams();
  if (opts.page) qs.set('page', String(opts.page));
  qs.set('limit', String(opts.limit ?? 25));
  if (opts.status) qs.set('status', opts.status);
  if (opts.from) qs.set('from', opts.from);
  if (opts.to) qs.set('to', opts.to);
  // Server envelope is `{ok, data: rows[], meta: {page, limit, total}}` and apiGet
  // unwraps `data`, so we only receive the rows array.
  return useQuery({
    queryKey: ['partners', partnerId, 'transactions', opts],
    enabled: !!partnerId,
    queryFn: () =>
      apiGet<PartnerTransactionRow[]>(`/partners/${partnerId}/transactions?${qs.toString()}`),
  });
}

// ────────────────────────── Opportunity applications summary ──────────────────────────
export interface OpportunityApplicationsSummary {
  opportunityId: string;
  total: number;
  byStatus: Record<string, number>;
  topColleges: Array<{ college: string | null; count: number }>;
  matchScore: { avg: number | null; scored: number };
}
export function useOpportunityApplicationsSummary(id: string | undefined) {
  return useQuery({
    queryKey: ['opportunities', 'applications-summary', id],
    enabled: !!id,
    queryFn: () => apiGet<OpportunityApplicationsSummary>(`/opportunities/${id}/applications-summary`),
  });
}

// ────────────────────────── Campus invitations (Wave 6 §K1-3) ──────────────────────────
export interface CampusInvitationRow {
  _id: string;
  tenant: string;
  status: 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired';
  partner?: { _id: string; name: string; logoUrl?: string } | string;
  college?: { _id: string; name: string; aisheCode?: string } | string;
  opportunity?: { _id: string; title?: string; type?: string } | string;
  preferredWindow?: { startsAt?: string; endsAt?: string; label?: string };
  note?: string;
  responseNote?: string;
  sentAt?: string;
  respondedAt?: string;
  createdAt: string;
}
export interface CampusInvitationsResponse {
  rows: CampusInvitationRow[];
  total: number;
  page: number;
  totalPages: number;
}
export function useCampusInvitations(opts: {
  status?: 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired' | '';
  page?: number;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (opts.status) qs.set('status', opts.status);
  if (opts.page) qs.set('page', String(opts.page));
  qs.set('limit', String(opts.limit ?? 50));
  return useQuery({
    queryKey: ['campus-invitations', opts],
    queryFn: () => apiGet<CampusInvitationsResponse>(`/campus-invitations?${qs.toString()}`),
  });
}
export function useRespondCampusInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; action: 'accept' | 'decline'; note?: string }) =>
      apiPost<{ invitation: CampusInvitationRow }>(
        `/campus-invitations/${v.id}/respond`,
        { action: v.action, note: v.note },
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['campus-invitations'] }); },
  });
}

export function useBookRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      from: string;
      to: string;
      purpose?: 'drive' | 'interview' | 'exam' | 'class' | 'other';
      driveId?: string;
      interviewId?: string;
      note?: string;
    }) =>
      apiPost<RoomRow>(`/calendar/rooms/${v.id}/book`, {
        from: v.from,
        to: v.to,
        purpose: v.purpose ?? 'other',
        driveId: v.driveId,
        interviewId: v.interviewId,
        note: v.note,
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['calendar', 'rooms'] }); },
  });
}

// ────────────────────────── Approvals (Axis-4) ──────────────────────────
export type ApprovalRegulator = 'AICTE' | 'UGC' | 'NAAC' | 'NBA' | 'NIRF' | 'PCI' | 'BCI' | 'MCI' | 'NCTE' | 'INC' | 'Other';
export type ApprovalStatus = 'active' | 'expired' | 'revoked' | 'superseded' | 'pending';
export type ApprovalSubjectKind = 'College' | 'University' | 'Program' | 'Specialization';

export interface RegulatorApprovalRow {
  _id: string;
  regulator: ApprovalRegulator;
  status: ApprovalStatus;
  approvalNumber?: string;
  title?: string;
  grantedTo?: string;
  validFrom?: string;
  validTo?: string;
  subject?: { kind: ApprovalSubjectKind; id: string };
  supersededBy?: string;
  revokedReason?: string;
}

export interface ApprovalListParams {
  page?: number;
  limit?: number;
  q?: string;
  regulator?: ApprovalRegulator | '';
  status?: ApprovalStatus | '';
  subjectKind?: ApprovalSubjectKind | '';
  subjectId?: string;
  sortBy?: 'validFrom' | 'validTo' | 'regulator' | 'status';
  sortDir?: 'asc' | 'desc';
}

export function useApprovals(params: ApprovalListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['group', 'approvals', params],
    queryFn: () => apiGet<{
      rows: RegulatorApprovalRow[];
      summary: { byRegulator: Record<string, number>; byStatus: Record<string, number> };
      meta?: { page: number; limit: number; total: number };
    }>(`/tenant/orgs/approvals?${qs.toString()}`),
  });
}

export interface ApprovalDetail {
  approval: RegulatorApprovalRow & {
    revokedReason?: string;
    gazetteRef?: string;
    certificateUrl?: string;
    remarks?: string;
    supersededBy?: string | null;
    tenant?: string;
    createdBy?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  subject: { kind: ApprovalSubjectKind; id: string; name: string; code?: string } | null;
  supersededBy: {
    _id: string; approvalNumber: string; title?: string;
    validFrom: string; status: ApprovalStatus;
  } | null;
}

export function useApprovalDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['group', 'approval', id],
    enabled: Boolean(id),
    queryFn: () => apiGet<ApprovalDetail>(`/tenant/orgs/approvals/${id}`),
  });
}

export function useRevokeApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<{ approval: RegulatorApprovalRow }>(`/tenant/orgs/approvals/${id}/revoke`, { reason }),
    onSuccess: (_out, { id }) => {
      qc.invalidateQueries({ queryKey: ['group', 'approval', id] });
      qc.invalidateQueries({ queryKey: ['group', 'approvals'] });
    },
  });
}

export function useSupersedeApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: {
      approvalNumber: string;
      validFrom: string;
      validUntil?: string;
      title?: string;
      gazetteRef?: string;
      certificateUrl?: string;
      remarks?: string;
    } }) =>
      apiPost<{ approval: RegulatorApprovalRow; superseded: RegulatorApprovalRow }>(
        `/tenant/orgs/approvals/${id}/supersede`, body,
      ),
    onSuccess: (_out, { id }) => {
      qc.invalidateQueries({ queryKey: ['group', 'approval', id] });
      qc.invalidateQueries({ queryKey: ['group', 'approvals'] });
    },
  });
}

// ────────────────────────── Governing entities ──────────────────────────
export type GoverningEntityType = 'Trust' | 'Society' | 'Section_8_Company' | 'Government' | 'Autonomous_Body' | 'Other';
export type GoverningEntityStatus = 'Active' | 'Inactive' | 'Suspended' | 'Dissolved';

export interface GoverningEntityRow {
  _id: string;
  entityName: string;
  entityType: GoverningEntityType;
  status: GoverningEntityStatus;
  registrationNumber?: string;
  panNumber?: string;
  registeredAt?: string;
  city?: string;
  state?: string;
}

export function useGoverningEntities(params: {
  page?: number; limit?: number; q?: string;
  entityType?: GoverningEntityType | ''; status?: GoverningEntityStatus | '';
  sortBy?: 'entityName' | 'entityType' | 'status'; sortDir?: 'asc' | 'desc';
} = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['group', 'governing-entities', params],
    queryFn: () => apiGet<{
      rows: GoverningEntityRow[];
      summary: { byType: Record<string, number> };
      meta?: { page: number; limit: number; total: number };
    }>(`/tenant/orgs/governing-entities?${qs.toString()}`),
  });
}

export interface GoverningEntityDetail {
  entity: GoverningEntityRow & {
    address?: { line1?: string; line2?: string; city?: string; state?: string; pincode?: string; country?: string };
    contactEmail?: string;
    contactPhone?: string;
    registeredAt?: string;
    remarks?: string;
  };
  linked: {
    colleges: Array<{ _id: string; name: string; code?: string; type?: string; tenant?: string }>;
    universities: Array<{ _id: string; name: string; type?: string; tenant?: string }>;
  };
}

export function useGoverningEntity(id: string | undefined) {
  return useQuery({
    queryKey: ['group', 'governing-entity', id],
    enabled: Boolean(id),
    queryFn: () => apiGet<GoverningEntityDetail>(`/tenant/orgs/governing-entities/${id}`),
  });
}

// ────────────────────────── Programs + Specializations ──────────────────────────
export interface ProgramRow {
  _id: string;
  name: string;
  code?: string;
  level?: string;
  status?: string;
  durationYears?: number;
  aisheCode?: string;
}

export function usePrograms(params: {
  page?: number; limit?: number; q?: string;
  level?: string; status?: string;
  sortBy?: 'name' | 'level' | 'status'; sortDir?: 'asc' | 'desc';
} = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['group', 'programs', params],
    queryFn: () => apiGet<{
      rows: ProgramRow[];
      summary?: Record<string, unknown>;
      meta?: { page: number; limit: number; total: number };
    }>(`/tenant/orgs/programs?${qs.toString()}`),
  });
}

export interface SpecializationRow {
  _id: string;
  // Server-authoritative names (Specialization model in the API).
  specializationName?: string;
  specializationCode?: string;
  shortName?: string;
  // Aliases some list handlers may return; keep both spellings usable.
  name?: string;
  code?: string;
  status?: string;
  program?: { _id: string; name?: string; programCode?: string; level?: string } | string;
}

export function useSpecializations(params: {
  page?: number; limit?: number; q?: string;
  programId?: string; status?: string;
  sortBy?: 'name' | 'status'; sortDir?: 'asc' | 'desc';
} = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['group', 'specializations', params],
    queryFn: () => apiGet<{
      rows: SpecializationRow[];
      summary?: Record<string, unknown>;
      meta?: { page: number; limit: number; total: number };
    }>(`/tenant/orgs/specializations?${qs.toString()}`),
  });
}

// ────────────────────────── Platform incidents ──────────────────────────
export type IncidentSeverity = 'sev1' | 'sev2' | 'sev3' | 'sev4';
export type IncidentStatus = 'declared' | 'investigating' | 'mitigating' | 'monitoring' | 'resolved' | 'postmortem';
export type IncidentCategory =
  | 'availability' | 'data' | 'security' | 'ai' | 'payments'
  | 'notifications' | 'assessments' | 'other';

export interface IncidentUpdateEntry {
  at: string;
  status: IncidentStatus;
  message: string;
  postedBy?: string | { _id: string; fullName?: string };
}

export interface IncidentRow {
  _id: string;
  incidentNumber: string;
  title: string;
  category: IncidentCategory;
  severity: IncidentSeverity;
  status: IncidentStatus;
  affectedTenants?: Array<{ _id: string; slug?: string; displayName?: string } | string>;
  platformWide?: boolean;
  affectedServices?: string[];
  startedAt: string;
  detectedAt?: string;
  declaredAt?: string;
  resolvedAt?: string;
  durationMinutes?: number;
  publicVisible?: boolean;
  rootCause?: string;
  postmortemUrl?: string;
  declaredBy?: { _id: string; fullName?: string; email?: string } | string;
  resolvedBy?: { _id: string; fullName?: string; email?: string } | string;
  updates?: IncidentUpdateEntry[];
}

export interface IncidentListParams {
  page?: number;
  limit?: number;
  q?: string;
  status?: IncidentStatus | '';
  severity?: IncidentSeverity | '';
  category?: IncidentCategory | '';
  affectedTenant?: string;
  platformWide?: 'true' | 'false' | '';
  from?: string;
  to?: string;
  sortBy?: 'startedAt' | 'declaredAt' | 'resolvedAt' | 'severity' | 'status';
  sortDir?: 'asc' | 'desc';
}

export function useIncidents(params: IncidentListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['platform', 'incidents', params],
    queryFn: () => apiGet<{
      rows: IncidentRow[];
      summary: { byStatus: Record<string, number>; bySeverity: Record<string, number> };
      meta?: { page: number; limit: number; total: number };
    }>(`/platform/incidents?${qs.toString()}`),
  });
}

export function useIncident(id: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'incidents', 'detail', id],
    queryFn: () => apiGet<IncidentRow>(`/platform/incidents/${id}`),
    enabled: Boolean(id),
  });
}

export interface DeclareIncidentInput {
  title: string;
  category: IncidentCategory;
  severity: IncidentSeverity;
  affectedTenants?: string[];
  platformWide?: boolean;
  affectedServices?: string[];
  publicVisible?: boolean;
  initialMessage: string;
}

export function useDeclareIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: DeclareIncidentInput) =>
      apiPost<IncidentRow>('/platform/incidents', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'incidents'] });
    },
  });
}

export interface IncidentUpdateInput {
  id: string;
  status: IncidentStatus;
  message: string;
  rootCause?: string;
  postmortemUrl?: string;
}

export function usePostIncidentUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: IncidentUpdateInput) =>
      apiPost<IncidentRow>(`/platform/incidents/${v.id}/updates`, {
        status: v.status, message: v.message,
        rootCause: v.rootCause, postmortemUrl: v.postmortemUrl,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'incidents'] });
    },
  });
}

// ────────────────────────── Integrity flags ──────────────────────────
export type IntegrityFlagKind =
  | 'answerMatch' | 'faceMismatch' | 'impossibleSpeed' | 'tabSwitch'
  | 'paste' | 'codeSimilarity' | 'networkTiming';
export type IntegrityFlagStatus = 'open' | 'cleared' | 'explained' | 'retest' | 'voided';
export type IntegrityFlagSeverity = 'low' | 'medium' | 'high';

export interface IntegrityFlagRow {
  _id: string;
  kind: IntegrityFlagKind;
  severity: IntegrityFlagSeverity;
  status: IntegrityFlagStatus;
  evidence?: Record<string, unknown>;
  reason?: string;
  createdAt: string;
  resolvedAt?: string;
  appealDeadline?: string;
  appealText?: string;
  appealSubmittedAt?: string;
  assessment?: { _id: string; name?: string; kind?: string } | string;
  primaryAttempt?: { _id: string; student?: string } | string;
}

export interface IntegrityFlagListParams {
  page?: number;
  limit?: number;
  status?: IntegrityFlagStatus | '';
  severity?: IntegrityFlagSeverity | '';
  kind?: IntegrityFlagKind | '';
  assessmentId?: string;
  studentId?: string;
  mine?: boolean;
  sortBy?: 'createdAt' | 'severity' | 'status' | 'appealDeadline';
  sortDir?: 'asc' | 'desc';
}

export interface IntegrityFlagsSummary {
  open: number;
  cleared: number;
  explained: number;
  retest: number;
  voided: number;
  appealing: number;
}

export function useIntegrityFlags(params: IntegrityFlagListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['integrity', 'flags', params],
    queryFn: () => apiGet<{
      rows: IntegrityFlagRow[];
      summary: IntegrityFlagsSummary;
      meta?: { page: number; limit: number; total: number };
    }>(`/assessments/integrity/flags?${qs.toString()}`),
  });
}

export function useAppealIntegrityFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; text: string }) =>
      apiPost<{ flagId: string; appealSubmittedAt: string }>(
        `/assessments/integrity/flags/${v.id}/appeal`,
        { text: v.text },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrity', 'flags'] });
    },
  });
}

export function useResolveIntegrityFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: 'cleared' | 'explained' | 'retest' | 'voided'; reason: string }) =>
      apiPost<{ flagId: string; status: string; appealDeadline?: string }>(
        `/assessments/integrity/flags/${v.id}/resolve`,
        { status: v.status, reason: v.reason },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrity', 'flags'] });
    },
  });
}

// ────────────────────────── Faculty directory ──────────────────────────
export type FacultyPhdStatus = 'holds_phd' | 'pursuing_phd' | 'not_pursuing';
export type FacultyType = 'permanent' | 'adjunct' | 'visiting' | 'guest' | 'contract';
export type FacultyStatus = 'active' | 'on_leave' | 'sabbatical' | 'retired' | 'terminated';

export interface FacultyRow {
  _id: string;
  user?: { _id: string; fullName: string; email: string } | string;
  department?: { _id: string; name: string; code?: string } | string;
  fullName: string;
  employeeId?: string;
  facultyType: FacultyType;
  status: FacultyStatus;
  phdStatus: FacultyPhdStatus;
  subjectsTaught?: string[];
  researchAreas?: string[];
  hIndex?: number;
}

export interface FacultyListParams {
  page?: number;
  limit?: number;
  q?: string;
  phdStatus?: FacultyPhdStatus | '';
  facultyType?: FacultyType | '';
  status?: FacultyStatus | '';
  department?: string;
  sortBy?: 'createdAt' | 'fullName' | 'hIndex';
  sortDir?: 'asc' | 'desc';
}

export function useFacultyDirectory(params: FacultyListParams = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null || (v as unknown) === false) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['faculty', 'directory', params],
    queryFn: () => apiGet<{
      rows: FacultyRow[];
      summary?: {
        byPhdStatus: Record<string, number>;
        byFacultyType: Record<string, number>;
      };
      meta?: { page: number; limit: number; total: number };
    }>(`/faculty/directory?${qs.toString()}`),
  });
}

export interface FacultyDetail extends FacultyRow {
  bio?: string;
  yearsOfExperience?: number;
  publications?: number;
  patents?: number;
  qualifications?: Array<{ degree: string; institution: string; year: number }>;
  awards?: string[];
}
export function useFacultyDetail(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['faculty', 'detail', id],
    queryFn: () => apiGet<{ profile: FacultyDetail }>(`/faculty/directory/${id}`),
  });
}

export function useMyAssignments(params: { page?: number; limit?: number; sortBy?: 'expiresAt' | 'createdAt'; sortDir?: 'asc' | 'desc' } = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    qs.set(k, String(v));
  });
  return useQuery({
    queryKey: ['student', 'me', 'assignments', params],
    queryFn: () => apiGet<{
      assignments: StudentAssignmentRow[];
      meta?: { page: number; limit: number; total: number };
    }>(`/students/me/assignments?${qs.toString()}`),
  });
}
